import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  clients as clientsApi, intakes as intakesApi, companies as companiesApi,
  offers, studyTypePrices, studyVariantItems, offerKinds, boreholes, fieldWorkKinds,
  type OfferInput, type OfferLineGroupInput, type OfferLineItemInput, type StudyTypePrice, type StudyVariantItem,
  type OfferKind, type OfferSectionInput, type OfferKindSectionTpl, type FieldWorkKindEntry,
} from '@/api/endpoints';
import { hasPermission, type Client, type Company, type Intake, type Offer, type OfferType, type OfferDeadlineMode, type OfferDocument } from '@/types';
import { useAuth } from '@/auth/AuthContext';
import { Icon } from '@/components/Icon';
import { OfferDocumentView } from '@/components/OfferDocumentView';
import { SearchableSelect } from '@/components/SearchableSelect';
import { ClientFormModal } from '@/components/ClientFormModal';
import { OfferAttachmentsPanel, uploadQueuedOfferFiles, type QueuedOfferFile } from '@/components/OfferAttachmentsPanel';
import { TiptapEditor } from '@/components/TiptapEditor';
import { formatMoney } from '@/lib/money';

// Kolejność: 0 Klient, 1 Cena (typy opracowań ze słownika), 2 Treść (po wybraniu zakresu),
// 3 Warunki, 4 Podsumowanie. Cena przed Treścią — bo treść opisuje już wybrany zakres prac.
const STEPS = ['Klient i firma', 'Cena', 'Treść', 'Warunki', 'Podsumowanie'];

/**
 * Lustro serwerowego {@code OfferDeadlineSupport.validationError} — żeby niekompletny termin
 * zatrzymywał kreator na właściwym kroku, a nie wracał jako surowe 400 po kliknięciu „Zapisz".
 */
function deadlineError(mode: OfferDeadlineMode, quarter: number | null,
                       year: number | null, text: string): string | null {
  if (mode === 'QUARTER') {
    if (!quarter || quarter < 1 || quarter > 4) return 'Wybierz kwartał (I–IV)';
    if (!year || year < 2000 || year > 2100) return 'Podaj rok kwartału (2000–2100)';
    return null;
  }
  if (mode === 'TEXT') {
    if (!text || !text.trim()) return 'Podaj opis terminu realizacji';
    if (text.trim().length > 200) return 'Opis terminu może mieć najwyżej 200 znaków';
  }
  return null;
}

/** Czy termin da się w ogóle wydrukować (lustro OfferDeadlineSupport.format). */
function deadlineLabel(mode: OfferDeadlineMode, date: string, quarter: number | null,
                       year: number | null, text: string): boolean {
  if (mode === 'DATE') return !!date;
  if (mode === 'QUARTER') return !!quarter && !!year;
  return !!text.trim();
}

/** Pozycja checkbox w sekcji listowej kreatora (z biblioteki rodzaju lub dopisana ręcznie). */
type WizSectionItem = { text: string; checked: boolean };
/** Sekcja treści w kreatorze; gdy ma `items` — renderowana jako lista pól wyboru (checkbox). */
type WizSection = OfferSectionInput & { items?: WizSectionItem[] };
const isListType = (rt: string) => rt === 'NUMBERED_LIST' || rt === 'BULLET_LIST';
/** Treść sekcji do zapisu: z zaznaczonych pozycji (checkbox) albo z pola tekstowego. */
function wizSectionContent(s: WizSection): string | undefined {
  if (s.items) {
    const picked = s.items.filter(it => it.checked && it.text.trim()).map(it => it.text.trim());
    return picked.length ? picked.join('\n') : undefined;
  }
  return s.content ?? undefined;
}

/** Zasiewa sekcję kreatora z szablonu rodzaju: sekcja listowa z biblioteką → lista checkboxów. */
function seedSection(s: OfferKindSectionTpl): WizSection {
  const hasLib = isListType(s.renderType) && !!s.items && s.items.length > 0;
  return {
    heading: s.heading ?? '',
    renderType: s.renderType,
    content: hasLib ? '' : (s.defaultContent ?? ''),
    items: hasLib
      ? s.items!.filter(it => it.active !== false)
          .slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
          .map(it => ({ text: it.text, checked: it.defaultChecked !== false }))
      : undefined,
  };
}

/** Stałe pozycje słownika „Cena za…” — forma jak w zdaniu „Ryczałtowa cena za …”. */
const UNIT_LABEL_BASE = ['operat', 'jedną studnię', 'szt.', 'komplet prac', 'opracowanie'];

const round2 = (n: number) => Math.round(n * 100) / 100;

const blankItem = (): OfferLineItemInput => ({
  description: '', groupLabel: null, unit: 'szt.', quantity: 1, unitPriceNet: 0,
  studyType: null, studyVariant: null, studyDeadline: null,
});

/** Friendly nazwa typu opracowania — z cennika (StudyTypePrice.studyTypeName), fallback na sam kod. */
function studyTypeFriendly(code: string | null | undefined, catalog: StudyTypePrice[]): string {
  if (!code) return '';
  const hit = catalog.find(p => p.studyType === code);
  return hit?.studyTypeName ?? code;
}

/** Sufiks tytułu z nazw wybranych ujęć: 1 → „dla ujęcia X"; kilka → „dla ujęć: X, Y". */
function intakeSuffixFor(names: string[]): string | undefined {
  if (names.length === 0) return undefined;
  if (names.length === 1) return `dla ujęcia ${names[0]}`;
  return `dla ujęć: ${names.join(', ')}`;
}

/** Buduje tytuł oferty z wybranych typów opracowań (zdeduplikowane) i sufiksu ujęć. */
function computeAutoTitle(
  type: OfferType,
  items: OfferLineItemInput[],
  lumpStudyType: string | null,
  lumpStudyVariant: string | null,
  catalog: StudyTypePrice[],
  intakeSuffix: string | undefined,
): string {
  const typesPart = (() => {
    if (type === 'LUMP_SUM') {
      if (!lumpStudyType) return '';
      const friendly = studyTypeFriendly(lumpStudyType, catalog);
      return lumpStudyVariant ? `${friendly} (${lumpStudyVariant})` : friendly;
    }
    // LINE_ITEMS — zdeduplikowana lista typów (wariant ignorujemy w tytule, byłby za długi)
    const uniq: string[] = [];
    for (const li of items) {
      if (!li.studyType) continue;
      if (!uniq.includes(li.studyType)) uniq.push(li.studyType);
    }
    if (uniq.length === 0) return '';
    const names = uniq.map(c => studyTypeFriendly(c, catalog));
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} + ${names[1]}`;
    // 3+ typów: pierwsze dwa + skrót
    return `${names[0]}, ${names[1]} +${names.length - 2} inne`;
  })();
  if (!typesPart) return '';
  return intakeSuffix ? `${typesPart} ${intakeSuffix}` : typesPart;
}

/**
 * Wypełnia szablon przedmiotu oferty („Na wykonanie…") danymi z kreatora i zwraca HTML (<p>).
 * Placeholdery: {ujecie} = nazwa ujęcia/ujęć, {miejscowosc} = " w miejscowości X" (gdy znana),
 * {studnia} = " nr {oznaczenie otworu}" (gdy wybrany; kilka studni: " nr 1, 2 i 3"). Puste placeholdery
 * znikają wraz z łącznikiem.
 */
function fillSubjectTemplate(
  template: string,
  intakeNames: string[],
  town: string | undefined,
  boreholeDesignations: string[],
): string {
  // Escapujemy wartości od użytkownika (nazwa ujęcia/miejscowość/otwór) — treść trafia do
  // subjectHtml (Jsoup w PDF/DOCX + dangerouslySetInnerHTML w podglądzie).
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const ujecie = esc(intakeNames.filter(Boolean).join(', '));
  const wells = boreholeDesignations.filter(Boolean).map(esc);
  const studnia = wells.length
    ? ` nr ${wells.length === 1 ? wells[0] : `${wells.slice(0, -1).join(', ')} i ${wells[wells.length - 1]}`}`
    : '';
  const miejscowosc = town ? ` w miejscowości ${esc(town)}` : '';
  let text = template
    .replace(/\{ujecie\}/g, ujecie)
    .replace(/\{studnia\}/g, studnia)
    .replace(/\{miejscowosc\}/g, miejscowosc);
  if (!ujecie) {
    // Puste {ujecie} (ujęcie jest opcjonalne) — usuń osierocony łącznik z pustym cudzysłowem „",
    // by nie zostało puste „" w przedmiocie oferty.
    text = text
      .replace(/\s*(?:na ujęciu wód podziemnych|z ujęcia|dla ujęcia wód podziemnych|dla ujęcia)\s*„\s*["”]/gi, '')
      .replace(/„\s*["”]/g, '');
  }
  text = text.replace(/ {2,}/g, ' ').replace(/\s+([.,])/g, '$1').trim();
  return `<p>${text}</p>`;
}

/** Prefill line itema z pozycji cennika; cena/ilość/opis edytowalne potem przez usera. */
const itemFromPrice = (p: StudyTypePrice): OfferLineItemInput => ({
  description: p.description ?? `${p.studyTypeName} — wariant ${p.variant}`,
  unit: p.unit,
  quantity: Number(p.defaultQuantity ?? 1),
  unitPriceNet: Number(p.unitPriceNet),
  studyType: p.studyType,
  studyVariant: p.variant,
  studyDeadline: null,
});

/** Mapuje podpowiedziany zestaw pozycji (słownik wariantu) na pozycje oferty. Pierwsza pozycja
 *  (główne opracowanie) niesie powiązanie z typem+wariantem; pozostałe to ad-hoc koszty towarzyszące. */
const itemsFromSuggestions = (
  suggestions: StudyVariantItem[], studyType: string, variant: string,
): OfferLineItemInput[] => suggestions.map((s, i) => ({
  description: s.description,
  unit: s.unit,
  quantity: Number(s.defaultQuantity) || 1,
  unitPriceNet: Number(s.unitPriceNet) || 0,
  studyType: i === 0 ? studyType : null,
  studyVariant: i === 0 ? variant : null,
  studyDeadline: null,
}));

const buildRecipientFromClient = (c: Client | undefined): string => {
  if (!c) return '';
  const lines: string[] = [c.name];
  if (c.addressLine1) lines.push(c.addressLine1);
  if (c.postalCode || c.city) lines.push([c.postalCode, c.city].filter(Boolean).join(' '));
  return lines.map(l => `<p>${escapeHtml(l)}</p>`).join('');
};

/** Czy kartoteka klienta ma cokolwiek z adresu — bez tego adresat na piśmie to sama nazwa. */
const clientHasAddress = (c: Client): boolean =>
  !!(c.addressLine1?.trim() || c.postalCode?.trim() || c.city?.trim());

/** Pusty edytor zwraca „<p></p>”, nie pusty napis. */
const isBlankHtml = (html: string): boolean => html.replace(/<[^>]*>/g, '').trim() === '';

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Domyślna data ważności oferty: dziś + 1 miesiąc. Format YYYY-MM-DD (input[type=date]).
 *  Date#setMonth obsługuje przejście na koniec miesiąca (31.01 + 1 miesiąc = 28/29.02). */
function defaultValidUntil(): string {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  return d.toISOString().slice(0, 10);
}

export function OfferWizard() {
  const nav = useNavigate();
  const { id: editId } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = !!editId;
  // Preselekcja klienta z ?clientId= (wejście z podglądu klienta) — nakładana raz, po wczytaniu klientów.
  const initialClientApplied = useRef(false);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [err, setErr] = useState<string | null>(null);

  // Reference data
  const [clients, setClients] = useState<Client[]>([]);
  const [intakes, setIntakes] = useState<Intake[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [clientModalOpen, setClientModalOpen] = useState(false);
  // Uzupełnianie adresu wybranego klienta wprost z kreatora — trafia do kartoteki,
  // więc w następnych ofertach adresat podstawi się sam.
  const [clientEditOpen, setClientEditOpen] = useState(false);
  const { user } = useAuth();
  const canEditClient = hasPermission(user, 'CLIENT_EDIT');
  // Wgrywanie załączników oferty to OFFER_EDIT (jak upload/usuwanie w OfferAttachmentApi).
  const canAttach = hasPermission(user, 'OFFER_EDIT');
  // Pliki od klienta (zapytanie z e-maila, karta otworu) — wgrywane po zapisaniu oferty.
  const [queuedFiles, setQueuedFiles] = useState<QueuedOfferFile[]>([]);
  const [priceCatalog, setPriceCatalog] = useState<StudyTypePrice[]>([]);
  // Ustawienia grup, kluczowane nazwą bez wielkości liter — tak samo jak unikalny indeks
  // w bazie (V173). Same grupy wynikają z etykiet wpisanych przy pozycjach; tutaj trzymamy
  // to, czego pozycja nie wie: typ opracowania, wariant i termin PRZYSZŁEGO ZLECENIA.
  const [groupSettings, setGroupSettings] = useState<Record<string, OfferLineGroupInput>>({});
  const [pricePickerOpen, setPricePickerOpen] = useState(false);
  // Cennik prac terenowych (V186) — osobny od cennika opracowań: jedna cena na rodzaj pracy.
  const [fieldWorkCatalog, setFieldWorkCatalog] = useState<FieldWorkKindEntry[]>([]);
  const [fieldWorkPickerOpen, setFieldWorkPickerOpen] = useState(false);

  // Step 0
  const [clientId, setClientId] = useState<number | ''>('');
  // Oferta może obejmować kilka ujęć (V71). Pierwsze = „główne".
  const [intakeIds, setIntakeIds] = useState<number[]>([]);
  const [companyId, setCompanyId] = useState<number | ''>('');
  // Default LINE_ITEMS — naturalny dla "wiele opracowań ze słownika cen".
  // Ryczałt zostaje dla prostych ofert na pojedynczą pracę.
  const [type, setType] = useState<OfferType>('LINE_ITEMS');
  // Rodzaj oferty (słownik V89) — ustawia domyślną wycenę, bloki treści, następstwo, format numeru.
  const [kindId, setKindId] = useState<number | ''>('');
  // Studnie ujęcia objęte ofertą (V187) — jedna lub kilka; pierwsza zaznaczona = główna
  // (z niej „Dane dotyczące studni" i domyślny otwór załączników).
  const [boreholeIds, setBoreholeIds] = useState<number[]>([]);
  const boreholeId = (boreholeIds.length ? boreholeIds[0] : '') as number | '';
  const [offerKindList, setOfferKindList] = useState<OfferKind[]>([]);
  const [boreholeOptions, setBoreholeOptions] = useState<{ id: number; designation: string; intakeId: number }[]>([]);
  // Lista studni pobrana dla bieżących ujęć — do komunikatu „ujęcie nie ma studni".
  const [boreholesLoaded, setBoreholesLoaded] = useState(false);
  const selectedDesignations = boreholeIds
    .map(id => boreholeOptions.find(b => b.id === id)?.designation)
    .filter(Boolean) as string[];
  const toggleBorehole = (id: number) =>
    setBoreholeIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  // Step 1
  const [title, setTitle] = useState('');
  // Tytuł generuje się automatycznie z wybranych typów + ujęcia, dopóki user nie zedytował
  // ręcznie. Mechanizm: trzymamy ostatni "auto" — jeśli aktualny title to on lub pusta wartość,
  // automat nadpisuje; inaczej zachowujemy ręczne wpisanie.
  const [lastAutoTitle, setLastAutoTitle] = useState('');
  const [recipientHtml, setRecipientHtml] = useState('');
  // Adresat z kartoteki klienta — jak tytuł: automat podmienia go, dopóki użytkownik go nie zmienił.
  const [lastAutoRecipient, setLastAutoRecipient] = useState('');
  const [subjectHtml, setSubjectHtml] = useState('');
  // Przedmiot („Na wykonanie…") auto-uzupełniany z szablonu rodzaju + danych oferty — analogicznie
  // do tytułu: dopóki user nie zmieni ręcznie, automat nadpisuje ostatnią auto-wartość.
  const [lastAutoSubject, setLastAutoSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [legalBasisHtml, setLegalBasisHtml] = useState('');

  // Step 2 — pricing
  const [unitPrice, setUnitPrice] = useState<number>(0);
  const [unitCount, setUnitCount] = useState<number>(1);
  const [unitLabel, setUnitLabel] = useState<string>('operat');
  const [usedUnitLabels, setUsedUnitLabels] = useState<string[]>([]);
  // LUMP_SUM — typ opracowania całej oferty (V29).
  const [lumpStudyType, setLumpStudyType] = useState<string | null>(null);
  const [lumpStudyVariant, setLumpStudyVariant] = useState<string | null>(null);
  const [lumpStudyDeadline, setLumpStudyDeadline] = useState<string>('');
  // Termin realizacji: data | kwartał | opis słowny (V160). Do V160 była wyłącznie data
  // i w ogóle nie trafiała na pismo — służyła tylko do skopiowania do Study.deadline.
  const [lumpDeadlineMode, setLumpDeadlineMode] = useState<OfferDeadlineMode>('DATE');
  const [lumpQuarter, setLumpQuarter] = useState<number | null>(null);
  const [lumpYear, setLumpYear] = useState<number | null>(null);
  const [lumpDeadlineText, setLumpDeadlineText] = useState<string>('');
  const [printDeadline, setPrintDeadline] = useState<boolean>(false);
  // Kwota VAT i brutto na piśmie (V160) — domyślnie tak, dla obu typów: pismo samo „netto
  // + należny podatek VAT” biuro uznało za nieczytelne (uwagi do OF-2026-019).
  const [showGross, setShowGross] = useState<boolean>(true);
  const [includeSignature, setIncludeSignature] = useState<boolean>(true);
  // Podgląd pisma składa SERWER (POST /api/offers/preview) — ten sam kod, z którego powstaje
  // PDF i DOCX. Wcześniej kreator miał własną, trzecią implementację wyglądu pisma i rozjeżdżał
  // się i z podglądem na karcie oferty, i z pobranym plikiem.
  const [previewDoc, setPreviewDoc] = useState<OfferDocument | null>(null);
  const [previewErr, setPreviewErr] = useState<string | null>(null);
  const [lumpPickerOpen, setLumpPickerOpen] = useState(false);
  const [items, setItems] = useState<OfferLineItemInput[]>([blankItem()]);
  // Pozycje ad-hoc doliczane do ceny ryczałtowej (demontaż pompy, wynajem agregatu…).
  // Osobno od `items` kosztorysu, żeby przełączanie typu tam i z powrotem ich nie mieszało.
  const [lumpExtras, setLumpExtras] = useState<OfferLineItemInput[]>([]);
  const [vatRate, setVatRate] = useState<number>(23);
  const [extraCostsHtml, setExtraCostsHtml] = useState('');
  // Sekcje treści oferty (V89) — zasiewane z rodzaju, edytowalne. Sekcje z biblioteką pozycji
  // (V91) pokazują listę pól wyboru: użytkownik zaznacza, co wchodzi w skład danej oferty.
  const [sections, setSections] = useState<WizSection[]>([]);
  // Jednorazowa rekonstrukcja checkboxów przy edycji (dopasowanie treści do biblioteki rodzaju).
  const sectionsReconstructed = useRef(false);

  // Step 3 — terms
  const [issueDate, setIssueDate] = useState<string>(new Date().toISOString().slice(0, 10));
  // Domyślna ważność oferty: dziś + 1 miesiąc. Edit-mode nadpisuje w useEffect z bazy.
  const [validUntil, setValidUntil] = useState<string>(() => defaultValidUntil());
  const [paymentTerms, setPaymentTerms] = useState<string>('Przelew 14 dni od daty wystawienia faktury.');
  const [termsHtml, setTermsHtml] = useState<string>('<p>Termin: do 1 miesiąca od daty otrzymania zlecenia.</p><p>Forma opracowania – w 2 egz. papierowej i jeden na nośniku CD (pdf).</p>');
  const [notes, setNotes] = useState<string>('');

  const selectedKind = offerKindList.find(k => k.id === kindId) ?? null;
  // Przedmiot oferty wybierany na samym początku: opracowanie albo prace terenowe. Zawęża listę
  // rodzajów; rodzaje „Inne” (np. legalizacja / operat) i klasyczna oferta bez rodzaju to opracowanie.
  const [purpose, setPurpose] = useState<'' | 'STUDY' | 'FIELDWORK'>('');
  const purposeOf = (k: OfferKind): 'STUDY' | 'FIELDWORK' => (k.purpose === 'FIELDWORK' ? 'FIELDWORK' : 'STUDY');
  const purposeKinds = offerKindList.filter(k => purposeOf(k) === purpose && (k.active || k.id === kindId));
  const choosePurpose = (p: 'STUDY' | 'FIELDWORK') => {
    if (p === purpose) return;
    setPurpose(p);
    // Jeden rodzaj dla przedmiotu — wybierz go od razu; inaczej zdejmij rodzaj z drugiego przedmiotu.
    const only = offerKindList.filter(k => purposeOf(k) === p && k.active);
    if (only.length === 1) { if (kindId !== only[0].id) onKindChange(String(only[0].id)); }
    else if (selectedKind && purposeOf(selectedKind) !== p) onKindChange('');
  };

  // Wybór rodzaju: ustaw domyślną wycenę + zasiej sekcje z aktywnych szablonów rodzaju.
  const onKindChange = (v: string) => {
    const id = v ? Number(v) : '';
    // Nie kasuj po cichu ręcznie wpisanych sekcji — potwierdź, jeśli mają treść.
    // Uwaga: dla sekcji listowych praca użytkownika żyje w `s.items` (zaznaczenia + własne pozycje),
    // a `s.content` bywa puste — dlatego guard uwzględnia też `items`, by nie skasować cicho wyboru.
    const hasSectionContent = sections.some(s =>
      (s.content && s.content.trim()) || (s.heading && s.heading.trim()) || (s.items && s.items.length > 0));
    setKindId(id);
    if (!id) {
      if (!hasSectionContent || confirm('Wyczyścić sekcje treści oferty?')) setSections([]);
      return;
    }
    const k = offerKindList.find(x => x.id === id);
    if (!k) return;
    if (k.defaultPricingMode === 'LUMP_SUM' || k.defaultPricingMode === 'LINE_ITEMS') {
      convertTypeTo(k.defaultPricingMode as OfferType);
    }
    const seeded: WizSection[] = (k.sections ?? [])
      .filter(s => s.active !== false)
      .slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map(s => seedSection(s));
    if (!hasSectionContent || confirm('Zmiana rodzaju zastąpi bieżące sekcje treści szablonem nowego rodzaju. Kontynuować?')) {
      setSections(seeded);
    }
  };

  useEffect(() => {
    if (!isEdit || loading || purpose) return;
    if (kindId === '') { setPurpose('STUDY'); return; }
    if (selectedKind) setPurpose(purposeOf(selectedKind));
  }, [isEdit, loading, purpose, kindId, selectedKind]);

  const addWizSection = () => setSections([...sections, { heading: '', renderType: 'RICH_TEXT', content: '' }]);
  const patchWizSection = (i: number, patch: Partial<OfferSectionInput>) =>
    setSections(sections.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const removeWizSection = (i: number) => setSections(sections.filter((_, idx) => idx !== i));
  const moveWizSection = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= sections.length) return;
    const next = [...sections];
    [next[i], next[j]] = [next[j], next[i]];
    setSections(next);
  };
  const fillSpecFromBorehole = async (i: number) => {
    if (boreholeId === '') return;
    try {
      const res = await offers.boreholeSpec(Number(boreholeId));
      patchWizSection(i, { content: res.content });
    } catch { /* ignore */ }
  };
  // --- Pozycje checkbox „Zakres prac" itp. ---
  const patchWizItems = (si: number, items: WizSectionItem[]) =>
    setSections(sections.map((s, idx) => (idx === si ? { ...s, items } : s)));
  const toggleWizItem = (si: number, ii: number) =>
    patchWizItems(si, (sections[si].items ?? []).map((it, j) => (j === ii ? { ...it, checked: !it.checked } : it)));
  const setWizItemText = (si: number, ii: number, text: string) =>
    patchWizItems(si, (sections[si].items ?? []).map((it, j) => (j === ii ? { ...it, text } : it)));
  const addWizItem = (si: number) =>
    patchWizItems(si, [...(sections[si].items ?? []), { text: '', checked: true }]);
  const removeWizItem = (si: number, ii: number) =>
    patchWizItems(si, (sections[si].items ?? []).filter((_, j) => j !== ii));
  const moveWizItem = (si: number, from: number, to: number) => {
    const items = [...(sections[si].items ?? [])];
    if (from === to || from < 0 || from >= items.length || to < 0 || to >= items.length) return;
    const [moved] = items.splice(from, 1);
    items.splice(to, 0, moved);
    patchWizItems(si, items);
  };
  // Drag&drop pozycji w obrębie sekcji — przeciąganie tylko za uchwyt, żeby nie psuć zaznaczania tekstu w polach.
  const [itemDragArmed, setItemDragArmed] = useState<string | null>(null);
  const [itemDrag, setItemDrag] = useState<{ si: number; ii: number } | null>(null);
  const [itemDragOver, setItemDragOver] = useState<{ si: number; ii: number } | null>(null);
  const endItemDrag = () => { setItemDrag(null); setItemDragOver(null); setItemDragArmed(null); };

  useEffect(() => {
    clientsApi.list().then(setClients).catch(() => {});
    companiesApi.list().then(cs => {
      setCompanies(cs);
      if (!isEdit) {
        const def = cs.find(c => c.isDefault) ?? cs[0];
        if (def) setCompanyId(def.id);
      }
    }).catch(() => {});
    studyTypePrices.list().then(setPriceCatalog).catch(() => { /* nonfatal */ });
    fieldWorkKinds.list().then(setFieldWorkCatalog).catch(() => { /* nonfatal */ });
    // includeInactive: w edycji oferta może wskazywać rodzaj później dezaktywowany — musi się rozwiązać.
    offerKinds.list(true).then(setOfferKindList).catch(() => { /* nonfatal */ });
    offers.unitLabels().then(ls => {
      setUsedUnitLabels(ls);
      // Nowa oferta: domyślnie najczęściej używany tekst — chyba że ktoś zdążył już zmienić „operat”.
      if (!isEdit && ls[0]) setUnitLabel(cur => (cur === 'operat' ? ls[0] : cur));
    }).catch(() => { /* nonfatal */ });
  }, [isEdit]);

  // Słownik „Cena za…”: stałe pozycje + teksty z dotychczasowych ofert (najczęstsze pierwsze).
  // Dopisany tekst trafia do słownika sam, gdy tylko oferta z nim zostanie zapisana.
  const unitLabelOptions = useMemo(() => {
    const seen = new Set<string>();
    return [...UNIT_LABEL_BASE, ...usedUnitLabels, unitLabel.trim()]
      .filter(l => l && !seen.has(l.toLowerCase()) && seen.add(l.toLowerCase()))
      .map(l => ({ value: l, label: l }));
  }, [usedUnitLabels, unitLabel]);

  /**
   * Zakres i cena z oferty: rodzaj, typ wyceny, ryczałt lub pozycje kosztorysu z grupami, VAT,
   * koszty dodatkowe i sekcje treści. Wspólne dla edycji i dla „Kopiuj z oferty innego ujęcia”.
   */
  const applyScopeAndPrice = (o: Offer) => {
    setType(o.type);
    setKindId(o.kindId ?? '');
    setExtraCostsHtml(o.extraCostsHtml ?? '');
    setUnitPrice(Number(o.unitPrice ?? 0));
    setUnitCount(o.unitCount ?? 1);
    setUnitLabel(o.unitLabel ?? '');
    setLumpStudyType(o.studyType ?? null);
    setLumpStudyVariant(o.studyVariant ?? null);
    setLumpStudyDeadline(o.studyDeadline ?? '');
    setLumpDeadlineMode(o.deadlineMode ?? 'DATE');
    setLumpQuarter(o.deadlineQuarter ?? null);
    setLumpYear(o.deadlineYear ?? null);
    setLumpDeadlineText(o.deadlineText ?? '');
    setGroupSettings(Object.fromEntries((o.lineGroups ?? []).map(g => [
      g.label.trim().replace(/\s+/g, ' ').toLowerCase(),
      {
        label: g.label,
        studyType: g.studyType ?? null,
        studyVariant: g.studyVariant ?? null,
        studyDeadline: g.studyDeadline ?? null,
        deadlineMode: g.deadlineMode ?? 'DATE',
        deadlineQuarter: g.deadlineQuarter ?? null,
        deadlineYear: g.deadlineYear ?? null,
        deadlineText: g.deadlineText ?? null,
      } as OfferLineGroupInput,
    ])));
    const loaded = (o.lineItems ?? []).map(li => ({
      description: li.description,
      groupLabel: li.groupLabel ?? null,
      unit: li.unit,
      quantity: li.quantity, unitPriceNet: li.unitPriceNet,
      studyType: li.studyType ?? null,
      studyVariant: li.studyVariant ?? null,
      studyDeadline: li.studyDeadline ?? null,
      deadlineMode: li.deadlineMode ?? 'DATE',
      deadlineQuarter: li.deadlineQuarter ?? null,
      deadlineYear: li.deadlineYear ?? null,
      deadlineText: li.deadlineText ?? null,
    }));
    // Przy ryczałcie pozycje to dopłaty ad-hoc, nie kosztorys.
    if (o.type === 'LUMP_SUM') { setLumpExtras(loaded); setItems([blankItem()]); }
    else { setItems(loaded.length ? loaded : [blankItem()]); setLumpExtras([]); }
    setVatRate(Number(o.vatRate ?? 23));
    sectionsReconstructed.current = false; // pozwól odtworzyć checkboxy z biblioteki rodzaju
    setSections((o.sections ?? []).map(s => ({
      heading: s.heading ?? '', renderType: s.renderType, content: s.content ?? '',
    })));
  };

  // Kopiowanie zakresu i ceny z oferty innego (podobnego) ujęcia — lista ofert ładowana przy pierwszym otwarciu.
  const [copySources, setCopySources] = useState<Offer[] | null>(null);
  const [copyInfo, setCopyInfo] = useState<string | null>(null);
  const loadCopySources = () => {
    if (copySources) return;
    offers.list().then(list => setCopySources(list.filter(o => !isEdit || o.id !== Number(editId))))
      .catch(() => setCopySources([]));
  };
  const copyFromOffer = async (v: string) => {
    if (!v) return;
    const hasWork = items.some(it => it.description?.trim()) || lumpExtras.length > 0 || unitPrice > 0
      || sections.some(sec => (sec.content ?? '').trim());
    if (hasWork && !confirm('Zastąpić bieżący zakres i cenę danymi z wybranej oferty?')) return;
    try {
      const o = await offers.get(Number(v));
      applyScopeAndPrice(o);
      setCopyInfo(`Skopiowano zakres i cenę z ${o.number}${o.intakes?.length ? ' (' + o.intakes.map(i => i.name).join(', ') + ')' : ''}. Sprawdź nazwy grup — mogą wskazywać studnie tamtego ujęcia.`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  // W trybie edycji — pobierz ofertę i populuj state
  useEffect(() => {
    if (!isEdit) return;
    setLoading(true); setErr(null);
    offers.get(Number(editId)).then(o => {
      // DRAFT zawsze; ACCEPTED tylko gdy nie ma jeszcze umowy (uzupełnianie ujęcia/typów post-akceptacja).
      const editable = o.status === 'DRAFT'
        || (o.status === 'ACCEPTED' && !o.convertedContractId);
      if (!editable) {
        setErr('Oferta w statusie ' + o.statusName + ' nie może być edytowana.');
        setLoading(false);
        return;
      }
      setClientId(o.client.id);
      setIntakeIds(o.intakes && o.intakes.length > 0
        ? o.intakes.map(i => i.id)
        : (o.intakeId ? [o.intakeId] : []));
      setCompanyId(o.company?.id ?? '');
      setTitle(o.title);
      setRecipientHtml(o.recipientHtml ?? '');
      setSubjectHtml(o.subjectHtml ?? '');
      setBodyHtml(o.bodyHtml ?? '');
      setLegalBasisHtml(o.legalBasisHtml ?? '');
      setTermsHtml(o.termsHtml ?? '');
      setPrintDeadline(o.printDeadline ?? false);
      setShowGross(o.showGross ?? false);
      setIncludeSignature(o.includeSignature ?? true);
      applyScopeAndPrice(o);
      setIssueDate(o.issueDate);
      setValidUntil(o.validUntil ?? '');
      setPaymentTerms(o.paymentTerms ?? '');
      setNotes(o.notes ?? '');
      setBoreholeIds(o.boreholes?.length ? o.boreholes.map(b => b.id) : o.boreholeId ? [o.boreholeId] : []);
      setLoading(false);
    }).catch(e => { setErr(e?.message ?? String(e)); setLoading(false); });
  }, [editId, isEdit]);

  // Edycja: gdy załaduje się słownik rodzajów, odtwórz stan checkboxów sekcji listowych,
  // dopasowując linie zapisanej treści do biblioteki pozycji rodzaju (linie spoza biblioteki
  // stają się własnymi, zaznaczonymi pozycjami). Uruchamiane jednorazowo na ofertę.
  useEffect(() => {
    if (!isEdit || sectionsReconstructed.current || kindId === '' || offerKindList.length === 0) return;
    const k = offerKindList.find(x => x.id === kindId);
    if (!k) return;
    sectionsReconstructed.current = true;
    const templates = k.sections ?? [];
    setSections(prev => {
      // usedTpl tworzony w środku updatera → funkcja pozostaje czysta (bezpieczne przy podwójnym
      // wywołaniu w React Strict Mode). Każdy szablon dopasowany najwyżej raz (powtórzone/puste nagłówki).
      const usedTpl = new Set<number>();
      return prev.map(sec => {
        const idx = templates.findIndex((t, ti) =>
          !usedTpl.has(ti) && (t.heading ?? '') === (sec.heading ?? '')
          && isListType(t.renderType) && (t.items?.length ?? 0) > 0);
        if (idx < 0) return sec;
        usedTpl.add(idx);
        const activeItems = (templates[idx].items ?? []).filter(it => it.active !== false);
        const lines = (sec.content ?? '').split(/\r?\n/).map(l => l.trim()).filter(Boolean);
        const libTexts = activeItems.map(it => it.text.trim());
        const libItems: WizSectionItem[] = activeItems
          .slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
          .map(it => ({ text: it.text, checked: lines.includes(it.text.trim()) }));
        const custom: WizSectionItem[] = lines.filter(l => !libTexts.includes(l)).map(text => ({ text, checked: true }));
        return { ...sec, content: '', items: [...libItems, ...custom] };
      });
    });
  }, [isEdit, kindId, offerKindList]);

  // Preselekcja klienta z query (?clientId=) przy tworzeniu z widoku klienta.
  // Czekamy aż lista klientów się wczyta, żeby efekt [clientId] (adresat/ujęcia) miał komplet danych.
  useEffect(() => {
    if (isEdit || initialClientApplied.current || clients.length === 0) return;
    const qp = searchParams.get('clientId');
    if (qp && clients.some(c => c.id === Number(qp))) {
      initialClientApplied.current = true;
      setClientId(Number(qp));
    }
  }, [isEdit, clients, searchParams]);

  useEffect(() => {
    if (!clientId) { setIntakes([]); setIntakeIds([]); return; }
    intakesApi.list().then(all => {
      const filtered = all.filter(u => u.client?.id === clientId);
      setIntakes(filtered);
      // Zachowaj tylko wybory należące do klienta (edycja: wczytane zostają; zmiana klienta czyści obce).
      setIntakeIds(prev => prev.filter(id => filtered.some(u => u.id === id)));
    }).catch(() => setIntakes([]));
  }, [clientId]);

  // Adresat z kartoteki klienta. Podmieniany, dopóki jest pusty albo równy ostatniemu
  // automatowi — także po zmianie klienta i po dopisaniu adresu w kartotece. Wcześniej
  // wypełniał się raz, przy pierwszym wyborze, i zostawał nieaktualny.
  useEffect(() => {
    const c = clients.find(x => x.id === clientId);
    if (!c) return;
    const computed = buildRecipientFromClient(c);
    if (isBlankHtml(recipientHtml) || recipientHtml === lastAutoRecipient) {
      setRecipientHtml(computed);
      setLastAutoRecipient(computed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, clients]);

  // Auto-generacja tytułu na podstawie wybranych typów opracowań + ujęcia.
  // Aktualizuje tylko gdy aktualny title jest pusty albo równy ostatnio auto-wygenerowanemu
  // (czyli user nie zmienił go ręcznie). Lookup nazw typów z priceCatalog (StudyType.studyTypeName).
  useEffect(() => {
    const names = intakeIds.map(id => intakes.find(u => u.id === id)?.name).filter(Boolean) as string[];
    const computed = computeAutoTitle(type, items, lumpStudyType, lumpStudyVariant, priceCatalog, intakeSuffixFor(names));
    if (!computed) return;
    if (title === '' || title === lastAutoTitle) {
      setTitle(computed);
      setLastAutoTitle(computed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, items, lumpStudyType, lumpStudyVariant, intakeIds, intakes, priceCatalog]);

  // Studnie wybranego ujęcia (i dodatkowych ujęć starszych ofert) do zaznaczenia.
  useEffect(() => {
    setBoreholesLoaded(false);
    if (!intakeIds.length) { setBoreholeOptions([]); setBoreholeIds([]); return; }
    let cancelled = false;
    Promise.all(intakeIds.map(intakeId => boreholes.listForIntake(intakeId)
      .then(list => list.map(b => ({ id: b.id, designation: b.designation, intakeId })))))
      .then(lists => {
        if (cancelled) return;
        const opts = lists.flat();
        setBoreholeOptions(opts);
        // Zmiana ujęcia unieważnia studnie poprzedniego — zostają tylko te, które nadal pasują.
        setBoreholeIds(prev => prev.filter(id => opts.some(o => o.id === id)));
        setBoreholesLoaded(true);
      })
      .catch(() => { if (!cancelled) setBoreholeOptions([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intakeIds]);

  // Auto-uzupełnienie przedmiotu („Na wykonanie…") z szablonu rodzaju + wybranego ujęcia/otworu.
  // Nadpisuje tylko gdy pusty albo równy ostatniemu auto (user nie zmienił ręcznie) — jak tytuł.
  useEffect(() => {
    const tpl = selectedKind?.subjectTemplate;
    if (!tpl) return;
    const names = intakeIds.map(id => intakes.find(u => u.id === id)?.name).filter(Boolean) as string[];
    const town = intakeIds[0] ? intakes.find(u => u.id === intakeIds[0])?.town ?? undefined : undefined;
    const computed = fillSubjectTemplate(tpl, names, town, selectedDesignations);
    if (subjectHtml.trim() === '' || subjectHtml === lastAutoSubject) {
      setSubjectHtml(computed);
      setLastAutoSubject(computed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKind?.subjectTemplate, intakeIds, intakes, boreholeIds, boreholeOptions]);

  // Auto-uzupełnienie sekcji „Dane dotyczące studni" (SPEC_TABLE) z wybranego otworu — puste tylko.
  useEffect(() => {
    if (boreholeId === '') return;
    if (!sections.some(s => s.renderType === 'SPEC_TABLE' && !(s.content ?? '').trim())) return;
    let cancelled = false;
    offers.boreholeSpec(Number(boreholeId)).then(res => {
      if (cancelled || !res.content) return;
      setSections(prev => prev.map(s =>
        s.renderType === 'SPEC_TABLE' && !(s.content ?? '').trim() ? { ...s, content: res.content } : s));
    }).catch(() => { /* nieblokujące */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boreholeId, kindId, sections.length]);

  const totals = useMemo(() => {
    const sumOf = (list: OfferLineItemInput[]) =>
      list.reduce((s, li) => s + (Number(li.quantity) || 0) * (Number(li.unitPriceNet) || 0), 0);
    const net = type === 'LUMP_SUM'
      ? round2((Number(unitPrice) || 0) * (Number(unitCount) || 1) + sumOf(lumpExtras))
      : round2(sumOf(items));
    const vat = round2(net * vatRate / 100);
    const gross = round2(net + vat);
    return { net, vat, gross };
  }, [type, unitPrice, unitCount, items, lumpExtras, vatRate]);

  // Etykiety grup już użyte w ofercie — do <datalist> przy polu „Grupa / studnia".
  // Bez podpowiedzi „Studnia S-1" i „Studnia S1" dałyby dwie grupy zamiast jednej.
  const groupSuggestions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const li of items) {
      const g = li.groupLabel?.trim().replace(/\s+/g, ' ');
      if (g) seen.set(g.toLowerCase(), g);
    }
    return Array.from(seen.values());
  }, [items]);

  const groupKey = (label: string) => label.trim().replace(/\s+/g, ' ').toLowerCase();
  const groupOf = (label: string): OfferLineGroupInput =>
    groupSettings[groupKey(label)] ?? { label, studyType: null, studyVariant: null, deadlineMode: 'DATE' };
  const updGroup = (label: string, patch: Partial<OfferLineGroupInput>) =>
    setGroupSettings(prev => {
      const k = groupKey(label);
      return { ...prev, [k]: { ...groupOf(label), label, ...patch } };
    });

  const updItem = (i: number, patch: Partial<OfferLineItemInput>) =>
    setItems(items.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const rmItem = (i: number) => setItems(items.length > 1 ? items.filter((_, idx) => idx !== i) : items);
  const addItem = () => setItems([...items, blankItem()]);
  // Kopia ląduje tuż pod oryginałem, a nie na końcu listy — przy kilku studniach z tym samym
  // zestawem prac zostaje do zmiany tylko grupa i to, co się różni (np. głębokość i cena).
  const dupItem = (i: number) =>
    setItems([...items.slice(0, i + 1), { ...items[i] }, ...items.slice(i + 1)]);

  const updExtra = (i: number, patch: Partial<OfferLineItemInput>) =>
    setLumpExtras(lumpExtras.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const rmExtra = (i: number) => setLumpExtras(lumpExtras.filter((_, idx) => idx !== i));
  const addExtra = () => setLumpExtras([...lumpExtras, blankItem()]);

  // Konwersja typu oferty (Kosztorys ↔ Ryczałt) bez utraty danych. Stan LINE_ITEMS (items)
  // i LUMP_SUM (unit*/lump*) żyją niezależnie, więc przełączanie tam i z powrotem nic nie gubi.
  // Przy przejściu na „pusty" tryb docelowy podpowiadamy jego treść ze źródłowego (seed).
  const lineItemsEmpty = () =>
    items.length === 0 || (items.length === 1 && !items[0].description.trim() && !items[0].studyType);
  const lumpEmpty = () => !lumpStudyType && !(Number(unitPrice) > 0);

  const convertTypeTo = (target: OfferType) => {
    if (target === type) return;
    if (target === 'LUMP_SUM') {
      if (lumpEmpty()) {
        const net = round2(items.reduce((s, li) => s + (Number(li.quantity) || 0) * (Number(li.unitPriceNet) || 0), 0));
        const firstStudy = items.find(li => li.studyType);
        if (net > 0) { setUnitPrice(net); setUnitCount(1); setUnitLabel('komplet'); }
        if (firstStudy) {
          setLumpStudyType(firstStudy.studyType ?? null);
          setLumpStudyVariant(firstStudy.studyVariant ?? null);
          setLumpStudyDeadline(firstStudy.studyDeadline ?? '');
          setLumpDeadlineMode(firstStudy.deadlineMode ?? 'DATE');
          setLumpQuarter(firstStudy.deadlineQuarter ?? null);
          setLumpYear(firstStudy.deadlineYear ?? null);
          setLumpDeadlineText(firstStudy.deadlineText ?? '');
        }
      }
    } else {
      if (lineItemsEmpty()) {
        const desc = (lumpStudyType ? studyTypeFriendly(lumpStudyType, priceCatalog) : '')
          || title.trim() || unitLabel.trim() || 'Pozycja ryczałtowa';
        // Pozycje ad-hoc przechodzą do kosztorysu jako zwykłe pozycje — kwota się nie zmienia.
        setItems([{
          description: desc,
          unit: unitLabel.trim() || 'komplet',
          quantity: Number(unitCount) || 1,
          unitPriceNet: Number(unitPrice) || 0,
          studyType: lumpStudyType,
          studyVariant: lumpStudyVariant,
          studyDeadline: lumpStudyDeadline || null,
          deadlineMode: lumpDeadlineMode,
          deadlineQuarter: lumpQuarter,
          deadlineYear: lumpYear,
          deadlineText: lumpDeadlineText || null,
        }, ...lumpExtras]);
      }
    }
    setErr(null);
    setType(target);
  };

  const validateStep = (s: number): string | null => {
    if (s === 0) {
      if (!purpose) return 'Wybierz przedmiot oferty: opracowanie czy prace terenowe';
      if (purpose === 'FIELDWORK' && kindId === '') return 'Wybierz rodzaj prac terenowych';
      if (!clientId) return 'Wybierz klienta';
      if (!companyId) return 'Wybierz firmę wystawiającą';
    }
    // Krok 1: Cena — wybór typów opracowań ze słownika (lub ryczałt jednoliniowy)
    if (s === 1) {
      if (type === 'LUMP_SUM') {
        if (!(unitPrice >= 0)) return 'Cena ryczałtowa nieprawidłowa';
        if (!(unitCount >= 1)) return 'Ilość musi być co najmniej 1';
        const de = deadlineError(lumpDeadlineMode, lumpQuarter, lumpYear, lumpDeadlineText);
        if (de) return `Termin realizacji: ${de}`;
        if (printDeadline && !deadlineLabel(lumpDeadlineMode, lumpStudyDeadline, lumpQuarter, lumpYear, lumpDeadlineText)) {
          return 'Zaznaczono druk terminu na ofercie, ale termin nie jest podany';
        }
        for (let i = 0; i < lumpExtras.length; i++) {
          const li = lumpExtras[i];
          if (!li.description.trim()) return `Pozycja ad-hoc ${i + 1}: opis wymagany`;
          if (!li.unit.trim()) return `Pozycja ad-hoc ${i + 1}: jednostka wymagana`;
          if (!(Number(li.quantity) > 0)) return `Pozycja ad-hoc ${i + 1}: ilość musi być dodatnia`;
        }
      } else {
        if (items.length === 0) return 'Dodaj co najmniej jedną pozycję';
        for (let i = 0; i < items.length; i++) {
          const li = items[i];
          if (!li.description.trim()) return `Pozycja ${i + 1}: opis wymagany`;
          if (!li.unit.trim()) return `Pozycja ${i + 1}: jednostka wymagana`;
          if (!(Number(li.quantity) > 0)) return `Pozycja ${i + 1}: ilość musi być dodatnia`;
          // Ta sama reguła co na serwerze — inaczej błąd wychodzi dopiero jako 400 przy
          // zapisie, a kreator zostaje na kroku „Podsumowanie" zamiast wrócić do pozycji.
          const lde = deadlineError(li.deadlineMode ?? 'DATE', li.deadlineQuarter ?? null,
                                    li.deadlineYear ?? null, li.deadlineText ?? '');
          if (lde) return `Pozycja ${i + 1} — termin: ${lde}`;
        }
      }
    }
    // Krok 2: Treść — tytuł i opisy do oferty
    if (s === 2) {
      if (!title.trim()) return 'Tytuł oferty jest wymagany';
    }
    if (s === 3 && validUntil && validUntil < issueDate) return 'Data ważności wcześniejsza niż wystawienia';
    return null;
  };

  const next = () => {
    const e = validateStep(step);
    if (e) { setErr(e); return; }
    setErr(null);
    setStep(Math.min(STEPS.length - 1, step + 1));
  };
  const prev = () => { setErr(null); setStep(Math.max(0, step - 1)); };

  const buildPayload = (): OfferInput => {
    const payload: OfferInput = {
      clientId: Number(clientId),
      intakeIds: intakeIds.length ? intakeIds : undefined,
      companyId: Number(companyId),
      type,
      title: title.trim(),
      scope: undefined,
      recipientHtml: recipientHtml || undefined,
      subjectHtml: subjectHtml || undefined,
      bodyHtml: bodyHtml || undefined,
      legalBasisHtml: legalBasisHtml || undefined,
      termsHtml: termsHtml || undefined,
      extraCostsHtml: extraCostsHtml || undefined,
      issueDate,
      validUntil: validUntil || undefined,
      paymentTerms: paymentTerms.trim() || undefined,
      vatRate,
      notes: notes.trim() || undefined,
      kindId: kindId === '' ? undefined : Number(kindId),
      boreholeIds,
      sections: sections.map((s, i) => ({
        position: i + 1,
        heading: s.heading?.trim() || undefined,
        renderType: s.renderType,
        content: wizSectionContent(s),
      })),
      showGross,
      includeSignature,
    };
    if (type === 'LUMP_SUM') {
      payload.unitPrice = unitPrice;
      payload.unitCount = unitCount;
      payload.unitLabel = unitLabel.trim() || undefined;
      payload.studyType = lumpStudyType ?? null;
      payload.studyVariant = lumpStudyVariant ?? null;
      payload.studyDeadline = lumpStudyDeadline || null;
      payload.deadlineMode = lumpDeadlineMode;
      payload.deadlineQuarter = lumpQuarter;
      payload.deadlineYear = lumpYear;
      payload.deadlineText = lumpDeadlineText || null;
      payload.printDeadline = printDeadline;
      payload.lineItems = lumpExtras.map(li => ({
        description: li.description.trim(),
        unit: li.unit.trim(),
        quantity: Number(li.quantity),
        unitPriceNet: Number(li.unitPriceNet),
      }));
    } else {
      // Grupy wynikają z etykiet użytych przy pozycjach — wysyłamy je jako osobną listę,
      // bo to na nich (a nie na pozycjach) wisi typ opracowania przyszłego zlecenia.
      payload.groups = groupSuggestions.map(label => {
        const g = groupOf(label);
        return {
          label,
          studyType: g.studyType ?? null,
          studyVariant: g.studyVariant ?? null,
          studyDeadline: g.studyDeadline ?? null,
          deadlineMode: g.deadlineMode ?? 'DATE',
          deadlineQuarter: g.deadlineQuarter ?? null,
          deadlineYear: g.deadlineYear ?? null,
          deadlineText: g.deadlineText ?? null,
        };
      });
      payload.lineItems = items.map(li => ({
        description: li.description.trim(),
        groupLabel: li.groupLabel?.trim() || null,
        unit: li.unit.trim(),
        quantity: Number(li.quantity),
        unitPriceNet: Number(li.unitPriceNet),
        studyType: li.studyType ?? null,
        studyVariant: li.studyVariant ?? null,
        studyDeadline: li.studyDeadline ?? null,
        deadlineMode: li.deadlineMode ?? 'DATE',
        deadlineQuarter: li.deadlineQuarter ?? null,
        deadlineYear: li.deadlineYear ?? null,
        deadlineText: li.deadlineText ?? null,
      }));
    }
    return payload;
  };

  // Podgląd składa serwer; odświeża się przy każdej zmianie danych, gdy jesteśmy na tym kroku.
  const previewKey = step === 4 ? JSON.stringify(buildPayload()) : null;
  useEffect(() => {
    if (previewKey === null) return;
    let cancelled = false;
    setPreviewErr(null);
    // Wyczyść poprzednie pismo — inaczej po zmianie danych widać STARĄ wersję (ze starymi
    // kwotami) aż do nadejścia odpowiedzi, bez żadnego znaku, że trwa przeliczanie.
    setPreviewDoc(null);
    offers.preview(JSON.parse(previewKey) as OfferInput)
      .then(d => { if (!cancelled) setPreviewDoc(d); })
      .catch(e => {
        if (cancelled) return;
        setPreviewDoc(null);
        setPreviewErr(e?.message ?? String(e));
      });
    return () => { cancelled = true; };
  }, [previewKey]);

  const submit = () => {
    for (let s = 0; s <= 3; s++) {
      const e = validateStep(s);
      if (e) { setErr(`Krok ${s + 1}: ${e}`); setStep(s); return; }
    }
    setBusy(true); setErr(null);
    const payload = buildPayload();
    const op = isEdit
      ? offers.update(Number(editId), payload)
      : offers.create(payload);
    op
      .then(async o => {
        // Oferta już zapisana — błąd pliku nie cofa zapisu; pokażemy go w podglądzie oferty,
        // gdzie plik da się dograć ponownie (powtórny „Zapisz" tutaj utworzyłby drugą ofertę).
        const attachmentErrors = queuedFiles.length
          ? await uploadQueuedOfferFiles(o.id, queuedFiles,
              selectedIntakes.map(u => ({ id: u.id, name: u.name, number: u.number })),
              boreholeId === '' ? null : Number(boreholeId))
          : [];
        nav(`/oferty/${o.id}`, attachmentErrors.length ? { state: { attachmentErrors } } : undefined);
      })
      .catch(e => { setErr(e?.message ?? String(e)); setBusy(false); });
  };

  const selectedClient = clients.find(c => c.id === clientId);
  const selectedCompany = companies.find(c => c.id === companyId);

  // Adresat bierze adres z kartoteki klienta. Gdy go tam nie ma, na piśmie zostawała sama
  // nazwa i adres trzeba było wpisywać ręcznie przy każdej ofercie dla tego klienta.
  const missingAddressNote = selectedClient && !clientHasAddress(selectedClient) && (
    <div className="p-2 border border-amber-200 bg-amber-50 rounded text-[12px] text-amber-900 flex items-start gap-2">
      <Icon name="bell" className="w-4 h-4 shrink-0 mt-0.5"/>
      <div>
        Kartoteka klienta <strong>{selectedClient.name}</strong> nie ma adresu, więc adresat na piśmie to sama nazwa.
        {canEditClient ? (
          <>
            {' '}<button type="button" onClick={() => setClientEditOpen(true)}
                         className="font-semibold underline hover:text-amber-700">Uzupełnij adres klienta</button>
            {' '}— zapisze się w kartotece i będzie podstawiany także w kolejnych ofertach.
          </>
        ) : ' Poproś o uzupełnienie adresu w kartotece klientów.'}
      </div>
    </div>
  );
  const selectedIntakes = intakeIds.map(id => intakes.find(u => u.id === id)).filter(Boolean) as Intake[];
  const selectedIntake = selectedIntakes[0];

  return (
    <div className="max-w-[1400px]">
      <header className="flex items-end justify-between mb-5">
        <div>
          <h1>{isEdit ? 'Edycja oferty' : 'Kreator oferty'}</h1>
          <div className="text-ink-400 text-[13px]">
            {isEdit ? 'Edytujesz istniejącą ofertę (tylko status DRAFT)' : 'Wycena prac w stylu pisma formalnego'}
          </div>
        </div>
      </header>

      {loading && <div className="text-ink-400 mb-3">Ładowanie oferty…</div>}

      <div className="flex items-center gap-2 mb-5 flex-wrap">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div className={'w-7 h-7 rounded-full grid place-items-center text-[12px] font-semibold ' + (i === step ? 'bg-water-500 text-white' : i < step ? 'bg-green-500 text-white' : 'bg-ink-100 text-ink-500')}>
              {i < step ? <Icon name="check" className="w-3.5 h-3.5"/> : i + 1}
            </div>
            <span className={'text-[12px] ' + (i === step ? 'text-ink-900 font-semibold' : 'text-ink-500')}>{s}</span>
            {i < STEPS.length - 1 && <span className="w-6 h-px bg-ink-200"/>}
          </div>
        ))}
      </div>

      {err && <div className="text-red-500 mb-3 text-[12px]">Błąd: {err}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-5">
        <div className="flex flex-col gap-4">

          {step === 0 && (
            <>
              <div className="card">
                <div className="card-head"><div className="card-title">Przedmiot oferty</div></div>
                <div className="card-body grid grid-cols-2 gap-2">
                  {([
                    { value: 'STUDY', label: 'Opracowanie', hint: 'Operat wodnoprawny, dokumentacja, projekt robót, analiza ryzyka…' },
                    { value: 'FIELDWORK', label: 'Prace terenowe', hint: 'Regeneracja, remont i inspekcja studni, pompowania, pomiary — ceny z cennika prac.' },
                  ] as const).map(o => (
                    <button key={o.value} type="button" onClick={() => choosePurpose(o.value)}
                      className={'border rounded-lg p-3 text-left transition ' + (purpose === o.value ? 'border-water-500 bg-water-500/5' : 'border-ink-200 hover:border-ink-300')}>
                      <div className="font-semibold text-ink-900 mb-1">{o.label}</div>
                      <div className="text-[11px] text-ink-500">{o.hint}</div>
                    </button>
                  ))}
                </div>
              </div>

              {purpose && (<>
              <div className="card">
                <div className="card-head"><div className="card-title">Firma wystawiająca</div></div>
                <div className="card-body">
                  <div className="grid grid-cols-2 gap-2">
                    {companies.map(c => (
                      <button key={c.id} type="button" onClick={() => setCompanyId(c.id)}
                        className={'border rounded-lg p-3 text-left transition flex items-center gap-3 ' +
                          (companyId === c.id ? 'border-water-500 bg-water-500/5' : 'border-ink-200 hover:border-ink-300')}>
                        {c.logoUrl
                          ? <img src={c.logoUrl} alt="" className="h-10 max-w-[80px] object-contain"/>
                          : <div className="w-12 h-10 grid place-items-center text-ink-300 text-[10px] bg-ink-50 rounded">brak logo</div>}
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-ink-900 text-[13px] truncate">{c.shortName}</div>
                          <div className="text-[11px] text-ink-500 truncate">{c.issuePlace} · NIP {c.nip ?? '—'}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                  {companies.length === 0 && (
                    <div className="text-ink-400 text-[12px] text-center py-4">Brak zdefiniowanych firm. Dodaj w <strong>Administracja → Konfiguracja → Firmy</strong>.</div>
                  )}
                </div>
              </div>

              <div className="card">
                <div className="card-head"><div className="card-title">Rodzaj oferty</div></div>
                <div className="card-body">
                  <label className="field">
                    <span className="field-label">Rodzaj (ustawia domyślną wycenę i bloki treści)</span>
                    <SearchableSelect value={kindId === '' ? '' : String(kindId)} onChange={onKindChange} clearable>
                      <option value="">{purpose === 'STUDY' ? '— oferta bez rodzaju (klasyczna) —' : '— wybierz rodzaj —'}</option>
                      {purposeKinds.map(k => (
                        <option key={k.id} value={k.id}>{k.name}</option>
                      ))}
                    </SearchableSelect>
                  </label>
                  {selectedKind && (
                    <div className="text-[11px] text-ink-400 mt-1">
                      {selectedKind.description ? selectedKind.description + ' ' : ''}
                      {selectedKind.followUpName ? `Po akceptacji: ${selectedKind.followUpName}.` : ''}
                    </div>
                  )}
                  {selectedKind?.usesBoreholeData && (
                    <div className="text-[11px] text-ink-400 mt-2">
                      „Dane studni” uzupełnią się z pierwszej zaznaczonej studni (karta „Klient i ujęcie”).
                    </div>
                  )}
                </div>
              </div>

              <div className="card">
                <div className="card-head"><div className="card-title">Typ oferty</div></div>
                <div className="card-body grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => convertTypeTo('LINE_ITEMS')}
                    className={'border rounded-lg p-3 text-left transition ' + (type === 'LINE_ITEMS' ? 'border-water-500 bg-water-500/5' : 'border-ink-200 hover:border-ink-300')}>
                    <div className="font-semibold text-ink-900 mb-1 flex items-center gap-2">
                      Kosztorys <span className="pill pill-green text-[9px]">zalecane</span>
                    </div>
                    <div className="text-[11px] text-ink-500">
                      Wiele opracowań w jednej ofercie, ceny wybierane ze słownika.
                      Konwertuje się na umowę z N&nbsp;zleceniami.
                    </div>
                  </button>
                  <button type="button" onClick={() => convertTypeTo('LUMP_SUM')}
                    className={'border rounded-lg p-3 text-left transition ' + (type === 'LUMP_SUM' ? 'border-water-500 bg-water-500/5' : 'border-ink-200 hover:border-ink-300')}>
                    <div className="font-semibold text-ink-900 mb-1">Ryczałt</div>
                    <div className="text-[11px] text-ink-500">
                      Jedna cena × ilość, bez podziału na opracowania. Dla prostych zleceń pojedynczych.
                    </div>
                  </button>
                </div>
                <div className="card-body pt-0 text-[11px] text-ink-400">
                  Typ można później zmienić w kroku „Cena" — wprowadzone pozycje i wartości zostają zachowane.
                </div>
              </div>

              </>)}

              <div className="card">
                <div className="card-head"><div className="card-title">Klient i ujęcie</div></div>
                <div className="card-body grid grid-cols-2 gap-3">
                  <label className="field col-span-2">
                    <span className="field-label flex items-center justify-between">
                      <span>Klient *</span>
                      <button type="button" onClick={() => setClientModalOpen(true)} className="text-water-600 hover:text-water-700 text-[11px] font-semibold flex items-center gap-1">
                        <Icon name="plus" className="w-3 h-3"/> Nowy klient
                      </button>
                    </span>
                    <SearchableSelect value={clientId === '' ? '' : String(clientId)} onChange={v => setClientId(v ? Number(v) : '')}>
                      <option value="">— wybierz klienta —</option>
                      {clients.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.city ? `${c.city} · ` : ''}{c.name}{c.nip ? ` · NIP ${c.nip}` : ''}
                        </option>
                      ))}
                    </SearchableSelect>
                  </label>
                  {missingAddressNote && <div className="col-span-2">{missingAddressNote}</div>}
                  <label className="field col-span-2">
                    <span className="field-label">Ujęcie (opcjonalnie)</span>
                    <SearchableSelect
                      value={intakeIds.length ? String(intakeIds[0]) : ''}
                      onChange={v => setIntakeIds(prev => v ? [Number(v), ...prev.slice(1).filter(x => x !== Number(v))] : prev.slice(1))}
                      disabled={!clientId}
                      clearable
                      placeholder="— wybierz ujęcie —">
                      <option value="">— wybierz ujęcie —</option>
                      {intakes.map(u => (
                        <option key={u.id} value={u.id}>{u.number} · {u.name}</option>
                      ))}
                    </SearchableSelect>
                    {intakeIds.length > 1 && (
                      // Starsze oferty bywały na kilka ujęć — pokazujemy je, żeby dało się je zdjąć.
                      <div className="flex flex-wrap items-center gap-1.5 mt-2 text-[11px] text-ink-500">
                        Oferta obejmuje też:
                        {intakeIds.slice(1).map(id => {
                          const u = intakes.find(x => x.id === id);
                          return (
                            <span key={id} className="pill pill-earth flex items-center gap-1 text-[11px]">
                              {u ? `${u.number} · ${u.name}` : `#${id}`}
                              <button type="button" onClick={() => setIntakeIds(prev => prev.filter(x => x !== id))}
                                      className="hover:text-red-500" title="Usuń ujęcie z oferty">
                                <Icon name="x" className="w-3 h-3"/>
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </label>
                  {intakeIds.length > 0 && (
                    <div className="field col-span-2">
                      <span className="field-label">Studnie objęte ofertą (jedna lub kilka)</span>
                      {boreholesLoaded && boreholeOptions.length === 0 ? (
                        <div className="text-[11px] text-amber-700">
                          To ujęcie nie ma jeszcze studni — dodaj je na{' '}
                          <a href={`/intakes/${intakeIds[0]}`} target="_blank" rel="noreferrer" className="underline">karcie ujęcia</a>.
                        </div>
                      ) : (
                        <div className="flex flex-col gap-1">
                          {boreholeOptions.map(b => {
                            const main = boreholeIds[0] === b.id;
                            const other = intakeIds.length > 1 && b.intakeId !== intakeIds[0]
                              ? intakes.find(u => u.id === b.intakeId) : undefined;
                            return (
                              <label key={b.id} className="flex items-center gap-2 text-[13px] cursor-pointer">
                                <input type="checkbox" checked={boreholeIds.includes(b.id)} onChange={() => toggleBorehole(b.id)}/>
                                <span>{b.designation}</span>
                                {other && <span className="text-[11px] text-ink-400">({other.name})</span>}
                                {main && boreholeIds.length > 1 && (
                                  <span className="text-[11px] text-ink-400" title="Studnia główna — źródło „Danych studni” i domyślny otwór załączników">★ główna</span>
                                )}
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  <label className="field col-span-2">
                    <span className="field-label">Kopiuj zakres i cenę z oferty innego (podobnego) ujęcia</span>
                    <div onFocusCapture={loadCopySources} onMouseDownCapture={loadCopySources}>
                      <SearchableSelect value="" onChange={copyFromOffer}
                                        placeholder={copySources ? '— wybierz ofertę —' : '— kliknij, aby wczytać oferty —'}>
                        <option value="">— wybierz ofertę —</option>
                        {(copySources ?? []).map(o => (
                          <option key={o.id} value={o.id}>
                            {o.number} · {(o.intakes ?? []).map(i => i.name).join(', ') || o.client?.name} · {o.title}
                          </option>
                        ))}
                      </SearchableSelect>
                    </div>
                    {copyInfo && <div className="text-[11px] text-ink-500 mt-1">{copyInfo}</div>}
                  </label>
                </div>
              </div>

              {canAttach && (
                <OfferAttachmentsPanel
                  offerId={isEdit ? Number(editId) : undefined}
                  intakes={selectedIntakes.map(u => ({ id: u.id, name: u.name, number: u.number }))}
                  defaultBoreholeId={boreholeId === '' ? null : Number(boreholeId)}
                  canEdit
                  queue={queuedFiles}
                  onQueueChange={setQueuedFiles}/>
              )}
            </>
          )}

          {step === 2 && (
            <>
              <div className="card">
                <div className="card-head"><div className="card-title">Tytuł (do listy ofert i numeracji)</div></div>
                <div className="card-body">
                  {(() => {
                    const names = intakeIds.map(id => intakes.find(u => u.id === id)?.name).filter(Boolean) as string[];
                    const auto = computeAutoTitle(type, items, lumpStudyType, lumpStudyVariant, priceCatalog, intakeSuffixFor(names));
                    const isAuto = title === lastAutoTitle && title !== '';
                    const canRegen = auto && auto !== title;
                    return (
                      <>
                        <input value={title} onChange={e => setTitle(e.target.value)} className="input" placeholder="np. Operat wodnoprawny – ujęcie Wiśniówka"/>
                        <div className="flex items-center gap-3 mt-1.5 text-[11px]">
                          {isAuto && auto && (
                            <span className="text-ink-400">ⓘ Wygenerowany automatycznie z wybranych typów. Edytuj swobodnie.</span>
                          )}
                          {canRegen && (
                            <button type="button"
                                    onClick={() => { setTitle(auto); setLastAutoTitle(auto); }}
                                    className="text-water-600 hover:text-water-800">
                              ↻ Wygeneruj automatycznie ({auto})
                            </button>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              <div className="card">
                <div className="card-head"><div className="card-title">Adresat (drukowane w ofercie)</div></div>
                <div className="card-body">
                  <TiptapEditor value={recipientHtml} onChange={setRecipientHtml} minHeight="120px"/>
                  <div className="flex items-center gap-3 mt-1 text-[11px]">
                    <span className="text-ink-400">Wypełniane z kartoteki klienta. Można dopisać „Pan/Pani Imię Nazwisko” itp.</span>
                    {selectedClient && buildRecipientFromClient(selectedClient) !== recipientHtml && (
                      <button type="button"
                              onClick={() => {
                                const auto = buildRecipientFromClient(selectedClient);
                                setRecipientHtml(auto);
                                setLastAutoRecipient(auto);
                              }}
                              className="text-water-600 hover:text-water-800 whitespace-nowrap">
                        ↻ Wstaw dane z kartoteki klienta
                      </button>
                    )}
                  </div>
                  {missingAddressNote && <div className="mt-2">{missingAddressNote}</div>}
                </div>
              </div>

              <div className="card">
                <div className="card-head"><div className="card-title">Sekcja "Na…" (przedmiot oferty)</div></div>
                <div className="card-body">
                  <TiptapEditor value={subjectHtml} onChange={setSubjectHtml} minHeight="120px"/>
                  {(() => {
                    const tpl = selectedKind?.subjectTemplate;
                    if (!tpl) {
                      return <div className="text-[11px] text-ink-400 mt-1">np. „Na wykonanie operatu wodnoprawnego dla nowoprojektowanej studni…"</div>;
                    }
                    const names = intakeIds.map(id => intakes.find(u => u.id === id)?.name).filter(Boolean) as string[];
                    const town = intakeIds[0] ? intakes.find(u => u.id === intakeIds[0])?.town ?? undefined : undefined;
                    const auto = fillSubjectTemplate(tpl, names, town, selectedDesignations);
                    const isAuto = subjectHtml === lastAutoSubject && subjectHtml !== '';
                    const canRegen = auto && auto !== subjectHtml;
                    return (
                      <div className="flex items-center gap-3 mt-1 text-[11px]">
                        {isAuto && <span className="text-ink-400">ⓘ Uzupełniony automatycznie z rodzaju i danych oferty. Edytuj swobodnie.</span>}
                        {canRegen && (
                          <button type="button" onClick={() => { setSubjectHtml(auto); setLastAutoSubject(auto); }}
                                  className="text-water-600 hover:text-water-800">↻ Uzupełnij automatycznie z danych</button>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>

              <div className="card">
                <div className="card-head"><div className="card-title">Treść uzupełniająca (opcjonalnie)</div></div>
                <div className="card-body">
                  <TiptapEditor value={bodyHtml} onChange={setBodyHtml} minHeight="160px"/>
                </div>
              </div>

              <div className="card">
                <div className="card-head"><div className="card-title">Podstawa prawna (opcjonalnie)</div></div>
                <div className="card-body">
                  <TiptapEditor value={legalBasisHtml} onChange={setLegalBasisHtml} minHeight="80px"/>
                  <div className="text-[11px] text-ink-400 mt-1">np. „Analiza ryzyka opracowana zgodnie z art. 133 ust. 3 ustawy Prawo Wodne…"</div>
                </div>
              </div>

              {(sections.length > 0 || selectedKind) && (
                <div className="card">
                  <div className="card-head">
                    <div className="card-title">Sekcje treści{selectedKind ? ` (${selectedKind.name})` : ''}</div>
                  </div>
                  <div className="card-body space-y-3">
                    {sections.length === 0 && (
                      <div className="text-[12px] text-ink-400">Brak sekcji. Dodaj blok treści (np. Dane studni, Zakres prac, Zapotrzebowanie).</div>
                    )}
                    {sections.map((s, i) => (
                      <div key={i} className="border border-ink-150 rounded-md p-2.5">
                        <div className="flex items-center gap-2 mb-1.5">
                          <input value={s.heading ?? ''} onChange={e => patchWizSection(i, { heading: e.target.value })}
                                 placeholder="Nagłówek (np. Zakres prac)" className="input flex-1 !py-1"/>
                          <select value={s.renderType} onChange={e => patchWizSection(i, { renderType: e.target.value })}
                                  className="input !py-1 w-48">
                            <option value="RICH_TEXT">Tekst sformatowany</option>
                            <option value="NUMBERED_LIST">Lista numerowana</option>
                            <option value="BULLET_LIST">Lista punktowana</option>
                            <option value="SPEC_TABLE">Dane techniczne</option>
                          </select>
                          <button type="button" onClick={() => moveWizSection(i, -1)} disabled={i === 0}
                                  className="text-ink-400 hover:text-ink-700 disabled:opacity-30 p-1" title="W górę"><Icon name="chevron-up" className="w-3.5 h-3.5"/></button>
                          <button type="button" onClick={() => moveWizSection(i, 1)} disabled={i === sections.length - 1}
                                  className="text-ink-400 hover:text-ink-700 disabled:opacity-30 p-1" title="W dół"><Icon name="chevron-down" className="w-3.5 h-3.5"/></button>
                          <button type="button" onClick={() => removeWizSection(i)}
                                  className="text-red-500 hover:text-red-700 p-1" title="Usuń"><Icon name="x" className="w-3.5 h-3.5"/></button>
                        </div>
                        {s.items ? (
                          <div className="space-y-1">
                            {s.items.length === 0 && (
                              <div className="text-[12px] text-ink-400">Brak pozycji — dodaj poniżej.</div>
                            )}
                            {s.items.map((it, ii) => {
                              const isDragged = itemDrag?.si === i && itemDrag.ii === ii;
                              const isOver = itemDrag?.si === i && itemDragOver?.si === i && itemDragOver.ii === ii && itemDrag.ii !== ii;
                              return (
                              <div key={ii}
                                   draggable={itemDragArmed === `${i}:${ii}`}
                                   onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', ''); setItemDrag({ si: i, ii }); }}
                                   onDragOver={e => { if (itemDrag?.si !== i) return; e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (!(itemDragOver && itemDragOver.si === i && itemDragOver.ii === ii)) setItemDragOver({ si: i, ii }); }}
                                   onDrop={e => { e.preventDefault(); if (itemDrag?.si === i) moveWizItem(i, itemDrag.ii, ii); endItemDrag(); }}
                                   onDragEnd={endItemDrag}
                                   className={'flex items-center gap-2 border-y-2 border-transparent '
                                     + (isDragged ? 'opacity-40 ' : '')
                                     + (isOver ? (itemDrag!.ii < ii ? '!border-b-water-500' : '!border-t-water-500') : '')}>
                                <span onMouseDown={() => setItemDragArmed(`${i}:${ii}`)} onMouseUp={() => setItemDragArmed(null)}
                                      className="text-ink-300 hover:text-ink-600 cursor-grab active:cursor-grabbing shrink-0 -mr-1" title="Przeciągnij, aby zmienić kolejność">
                                  <Icon name="grip" className="w-3.5 h-3.5"/>
                                </span>
                                <input type="checkbox" checked={it.checked} onChange={() => toggleWizItem(i, ii)} className="shrink-0 cursor-pointer"/>
                                <input value={it.text} onChange={e => setWizItemText(i, ii, e.target.value)}
                                       className={'input flex-1 !py-0.5 text-[13px] ' + (it.checked ? '' : 'opacity-45 line-through')}
                                       placeholder="Treść pozycji"/>
                                <button type="button" onClick={() => removeWizItem(i, ii)}
                                        className="text-red-400 hover:text-red-700 p-0.5 shrink-0" title="Usuń pozycję"><Icon name="x" className="w-3.5 h-3.5"/></button>
                              </div>
                              );
                            })}
                            <div className="flex items-center justify-between pt-0.5">
                              <button type="button" onClick={() => addWizItem(i)}
                                      className="text-water-600 hover:text-water-800 text-[12px]">
                                <Icon name="plus" className="w-3 h-3 inline"/> Dodaj własną pozycję
                              </button>
                              <span className="text-[11px] text-ink-400">
                                Zaznaczono {s.items.filter(x => x.checked && x.text.trim()).length} z {s.items.length} — do oferty trafią zaznaczone.
                              </span>
                            </div>
                          </div>
                        ) : s.renderType === 'RICH_TEXT' ? (
                          <TiptapEditor value={s.content ?? ''} onChange={html => patchWizSection(i, { content: html })} minHeight="100px"/>
                        ) : (
                          <>
                            <textarea value={s.content ?? ''} onChange={e => patchWizSection(i, { content: e.target.value })}
                                      className="input w-full !py-1 text-[13px]" rows={s.renderType === 'SPEC_TABLE' ? 5 : 4}
                                      placeholder={s.renderType === 'SPEC_TABLE'
                                        ? 'Klucz: Wartość (jedna para w wierszu), np. Głębokość: 25,9 m'
                                        : 'Jedna pozycja w wierszu'}/>
                            {s.renderType === 'SPEC_TABLE' && selectedKind?.usesBoreholeData && boreholeId !== '' && (
                              <button type="button" onClick={() => fillSpecFromBorehole(i)}
                                      className="text-water-600 hover:text-water-800 text-[11px] mt-1">↻ Uzupełnij z otworu</button>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                    <button type="button" onClick={addWizSection} className="btn btn-secondary btn-sm">
                      <Icon name="plus" className="w-3 h-3"/> Dodaj sekcję
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {step === 1 && (
            <div className="card">
              <div className="card-body flex items-center gap-3 flex-wrap py-3">
                <span className="text-[12px] font-semibold text-ink-700">Typ oferty:</span>
                <div className="inline-flex rounded-lg border border-ink-200 overflow-hidden">
                  <button type="button" onClick={() => convertTypeTo('LINE_ITEMS')}
                    className={'px-3 py-1.5 text-[12px] font-medium transition ' +
                      (type === 'LINE_ITEMS' ? 'bg-water-500 text-white' : 'bg-white text-ink-600 hover:bg-ink-50')}>
                    Kosztorys
                  </button>
                  <button type="button" onClick={() => convertTypeTo('LUMP_SUM')}
                    className={'px-3 py-1.5 text-[12px] font-medium transition border-l border-ink-200 ' +
                      (type === 'LUMP_SUM' ? 'bg-water-500 text-white' : 'bg-white text-ink-600 hover:bg-ink-50')}>
                    Ryczałt
                  </button>
                </div>
                <span className="text-[11px] text-ink-400">
                  <Icon name="check" className="w-3 h-3 inline -mt-0.5 mr-0.5"/>
                  Zmiana typu zachowuje wprowadzone pozycje i wartości.
                </span>
              </div>
            </div>
          )}

          {step === 1 && type === 'LUMP_SUM' && (
            <>
              <div className="p-3 border border-amber-200 bg-amber-50 rounded text-[12px] text-amber-900 flex items-start gap-2">
                <Icon name="bell" className="w-4 h-4 shrink-0 mt-0.5"/>
                <div>
                  Tryb <strong>Ryczałt</strong> przyjmuje jedną cenę za całość; dodatkowe prace z własną ceną dopiszesz niżej
                  jako <strong>pozycje ad-hoc</strong>. Aby rozbić ofertę na <strong>wiele opracowań różnych typów</strong>
                  {' '}z cenami ze słownika, przełącz typ na <strong>Kosztorys</strong> powyżej — wprowadzone wartości zostaną zachowane.
                </div>
              </div>

              <div className="card">
                <div className="card-head">
                  <div className="card-title">Typ opracowania (ze słownika)</div>
                  <button type="button" onClick={() => setLumpPickerOpen(true)} className="ml-auto btn btn-primary btn-sm">
                    <Icon name="plus" className="w-3.5 h-3.5"/>
                    {lumpStudyType ? 'Zmień typ' : 'Wybierz typ ze słownika'}
                  </button>
                </div>
                <div className="card-body">
                  {lumpStudyType ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="pill pill-earth">{lumpStudyType}{lumpStudyVariant ? ' · ' + lumpStudyVariant : ''}</span>
                      <span className="text-[12px] text-ink-500">— wybranie pozycji ze słownika prefilluje cenę poniżej; wartości zostają edytowalne.</span>
                      <button type="button"
                              onClick={() => { setLumpStudyType(null); setLumpStudyVariant(null); setLumpStudyDeadline(''); }}
                              className="text-ink-400 hover:text-red-500 p-1"
                              title="Wyczyść powiązanie z typem opracowania">
                        <Icon name="x" className="w-3.5 h-3.5"/>
                      </button>
                    </div>
                  ) : (
                    <div className="text-[12px] text-ink-500 italic">
                      Brak wybranego typu — przy konwersji oferty na umowę utworzone zlecenie dostanie domyślny typ wg ujęcia.
                      Kliknij „Wybierz typ ze słownika", aby określić docelowy typ opracowania.
                    </div>
                  )}
                </div>
              </div>

              <div className="card">
                <div className="card-head"><div className="card-title">Cena ryczałtowa</div></div>
                <div className="card-body grid grid-cols-3 gap-3">
                  <label className="field">
                    <span className="field-label">Cena netto za jednostkę</span>
                    <input type="number" step="0.01" value={unitPrice} onChange={e => setUnitPrice(Number(e.target.value))} className="input font-mono text-right"/>
                  </label>
                  <label className="field">
                    <span className="field-label">Ilość</span>
                    <input type="number" step="1" min="1" value={unitCount} onChange={e => setUnitCount(Number(e.target.value))} className="input font-mono text-right"/>
                  </label>
                  <label className="field">
                    <span className="field-label">Cena za… (tekst na piśmie)</span>
                    <SearchableSelect value={unitLabel} onChange={setUnitLabel} options={unitLabelOptions}
                                      allowCreate allowFreeValue clearable placeholder="— bez „za …” —"
                                      searchPlaceholder="Szukaj lub wpisz nowy tekst…"/>
                  </label>
                  {/* Rodzaju i przypadka nie da się odgadnąć — wpisany tekst idzie na pismo bez zmian. */}
                  <div className="col-span-3 -mt-1 text-[11px] text-ink-400">
                    Na piśmie: „Ryczałtowa cena{unitLabel.trim() ? <> za <strong className="text-ink-600">{unitLabel.trim()}</strong></> : ''} (netto): …”
                    {' '}— wpisz tak, jak ma brzmieć zdanie, np. „jedną studnię”.
                  </div>
                  <label className="field">
                    <span className="field-label">VAT [%]</span>
                    <input type="number" step="0.01" value={vatRate} onChange={e => setVatRate(Number(e.target.value))} className="input font-mono"/>
                  </label>
                  <div className="col-span-2">
                    <DeadlineFields
                      label="Termin realizacji"
                      hint="Termin trafia na pismo tylko po zaznaczeniu opcji niżej. Do terminu opracowania (po konwersji na zlecenie) przenosi się konkretna data — dla kwartału jego ostatni dzień; tryb „Opis” daty nie ma."
                      mode={lumpDeadlineMode} onMode={setLumpDeadlineMode}
                      date={lumpStudyDeadline} onDate={setLumpStudyDeadline}
                      quarter={lumpQuarter} onQuarter={setLumpQuarter}
                      year={lumpYear} onYear={setLumpYear}
                      text={lumpDeadlineText} onText={setLumpDeadlineText}
                    />
                    <label className="flex items-center gap-2 text-[12px] text-ink-700 mt-2 cursor-pointer">
                      <input type="checkbox" checked={printDeadline} onChange={e => setPrintDeadline(e.target.checked)}/>
                      Drukuj termin realizacji na ofercie
                    </label>
                    {printDeadline && termsHtml.toLowerCase().includes('termin') && (
                      <div className="text-[11px] text-amber-700 mt-1">
                        Uwaga: sekcja „Termin / forma opracowania” (krok „Warunki”) też wspomina o terminie —
                        na jednym piśmie wyjdą dwa.
                      </div>
                    )}
                  </div>
                  <label className="flex items-center gap-2 text-[12px] text-ink-700 col-span-2 cursor-pointer">
                    <input type="checkbox" checked={showGross} onChange={e => setShowGross(e.target.checked)}/>
                    Pokaż kwotę brutto na ofercie
                    <span className="text-ink-400">(dopisze podatek VAT i wartość brutto pod ceną)</span>
                  </label>
                </div>
              </div>

              <div className="card">
                <div className="card-head">
                  <div>
                    <div className="card-title">Pozycje ad-hoc (opcjonalnie)</div>
                    <div className="text-[11px] text-ink-400 mt-0.5">
                      Dodatkowe prace z własną ceną, np. demontaż agregatu pompowego. Na piśmie stoją pod ceną
                      ryczałtową i wchodzą do kwoty netto, VAT i brutto.
                    </div>
                  </div>
                </div>
                <div className="card-body flex flex-col gap-2">
                  {lumpExtras.map((li, i) => (
                    <div key={i} className="border border-ink-200 rounded-lg p-3 flex flex-col gap-2 bg-ink-50/40">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[12px] text-ink-400">#{i + 1}</span>
                        <span className="ml-auto font-mono tabular-nums text-[13px] font-semibold text-ink-800">
                          {formatMoney(round2((Number(li.quantity) || 0) * (Number(li.unitPriceNet) || 0)))} zł
                        </span>
                        <button type="button" onClick={() => rmExtra(i)}
                                className="btn btn-ghost btn-icon text-ink-400 hover:text-red-500" title="Usuń pozycję">
                          <Icon name="x"/>
                        </button>
                      </div>
                      <textarea value={li.description} onChange={e => updExtra(i, { description: e.target.value })}
                                rows={2} className="textarea !py-1.5 w-full text-[13px] leading-snug"
                                placeholder="Opis pozycji, np. Demontaż i montaż agregatu pompowego"/>
                      <div className="grid grid-cols-3 gap-2">
                        <label className="field">
                          <span className="field-label">Jednostka</span>
                          <input value={li.unit} onChange={e => updExtra(i, { unit: e.target.value })} className="input !py-1 font-mono text-[12px]"/>
                        </label>
                        <label className="field">
                          <span className="field-label">Ilość</span>
                          <input type="number" step="0.001" value={li.quantity} onChange={e => updExtra(i, { quantity: Number(e.target.value) })} className="input !py-1 font-mono text-[12px] text-right"/>
                        </label>
                        <label className="field">
                          <span className="field-label">Cena netto</span>
                          <input type="number" step="0.01" value={li.unitPriceNet} onChange={e => updExtra(i, { unitPriceNet: Number(e.target.value) })} className="input !py-1 font-mono text-[12px] text-right"/>
                        </label>
                      </div>
                    </div>
                  ))}
                  <button type="button" onClick={addExtra} className="btn btn-secondary btn-sm self-start">
                    <Icon name="plus" className="w-3.5 h-3.5"/> Dodaj pozycję ad-hoc
                  </button>
                </div>
              </div>

              <div className="card">
                <div className="card-head"><div className="card-title">Dodatkowe koszty po stronie Zamawiającego (opcjonalnie)</div></div>
                <div className="card-body">
                  <TiptapEditor value={extraCostsHtml} onChange={setExtraCostsHtml} minHeight="80px"/>
                  <div className="text-[11px] text-ink-400 mt-1">np. „Opłaty za wydanie pozwolenia wp. – 330,07 zł, po stronie Zamawiającego."</div>
                </div>
              </div>
            </>
          )}

          {step === 1 && type === 'LINE_ITEMS' && (
            <div className="card">
              <div className="card-head">
                <div>
                  <div className="card-title">Pozycje kosztorysu</div>
                  <div className="text-[11px] text-ink-400 mt-0.5">
                    Wybierz typ opracowania i wariant (prosty/złożony) — system podpowie zestaw pozycji do przycięcia i edycji cen.
                    Pozycje z tą samą <strong>grupą</strong> (np. „Studnia S-1") dostaną na ofercie wspólny nagłówek i własną podsumę.
                  </div>
                </div>
                <div className="ml-auto flex gap-2">
                  {/* Oferta terenowa zaczyna od cennika prac — ten przycisk wychodzi wtedy na pierwszy plan. */}
                  <button type="button" onClick={() => setFieldWorkPickerOpen(true)}
                          className={'btn btn-sm ' + (selectedKind?.purpose === 'FIELDWORK' ? 'btn-primary' : 'btn-secondary')}>
                    <Icon name="plus" className="w-3.5 h-3.5"/> Z cennika prac terenowych
                  </button>
                  <button type="button" onClick={() => setPricePickerOpen(true)}
                          className={'btn btn-sm ' + (selectedKind?.purpose === 'FIELDWORK' ? 'btn-secondary' : 'btn-primary')}>
                    <Icon name="plus" className="w-3.5 h-3.5"/> Podpowiedz pozycje wg wariantu
                  </button>
                </div>
              </div>
              <div className="card-body flex flex-col gap-2">
                {groupSuggestions.length > 0 && (
                  <div className="border border-water-200 bg-water-500/5 rounded-lg p-3 flex flex-col gap-3">
                    <div className="text-[12px] font-semibold text-ink-800">
                      Grupy — z każdej powstanie osobne zlecenie
                      <span className="font-normal text-ink-500 ml-1">
                        (bez typu opracowania grupa jest tylko rozbiciem ceny na piśmie)
                      </span>
                    </div>
                    {groupSuggestions.map(label => {
                      const g = groupOf(label);
                      const suma = items
                        .filter(it => it.groupLabel && groupKey(it.groupLabel) === groupKey(label))
                        .reduce((sm, it) => sm + (Number(it.quantity) || 0) * (Number(it.unitPriceNet) || 0), 0);
                      const warianty = Array.from(new Set(priceCatalog
                        .filter(pc => pc.studyType === g.studyType).map(pc => pc.variant)));
                      return (
                        <div key={groupKey(label)} className="flex flex-col gap-2 border-t border-water-200/60 pt-2 first:border-0 first:pt-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-[13px] text-ink-900">{label}</span>
                            <span className="ml-auto font-mono tabular-nums text-[13px] text-ink-700">
                              {formatMoney(round2(suma))} zł
                            </span>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            <label className="field">
                              <span className="field-label">Typ opracowania</span>
                              <SearchableSelect value={g.studyType ?? ''} clearable
                                onChange={v => updGroup(label, { studyType: v || null, studyVariant: null })}
                                placeholder="— bez zlecenia —">
                                <option value="">— bez zlecenia —</option>
                                {Array.from(new Map(priceCatalog.map(pc => [pc.studyType, pc.studyTypeName])).entries())
                                  .map(([code, name]) => <option key={code} value={code}>{name}</option>)}
                              </SearchableSelect>
                            </label>
                            <label className="field">
                              <span className="field-label">Wariant</span>
                              <SearchableSelect value={g.studyVariant ?? ''} clearable
                                onChange={v => updGroup(label, { studyVariant: v || null })}
                                disabled={!g.studyType} placeholder={g.studyType ? '— domyślny —' : '—'}>
                                <option value="">— domyślny —</option>
                                {warianty.map(v => <option key={v} value={v}>{variantLabel(v)}</option>)}
                              </SearchableSelect>
                            </label>
                            <div className="field">
                              <span className="field-label">Termin zlecenia</span>
                              <DeadlineFields compact
                                mode={g.deadlineMode ?? 'DATE'} onMode={m => updGroup(label, { deadlineMode: m })}
                                date={g.studyDeadline ?? ''} onDate={v => updGroup(label, { studyDeadline: v || null })}
                                quarter={g.deadlineQuarter ?? null} onQuarter={v => updGroup(label, { deadlineQuarter: v })}
                                year={g.deadlineYear ?? null} onYear={v => updGroup(label, { deadlineYear: v })}
                                text={g.deadlineText ?? ''} onText={v => updGroup(label, { deadlineText: v || null })}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* Jedna lista podpowiedzi na całą kartę — żeby „Studnia S-1" wpisana raz
                    podpowiadała się w kolejnych pozycjach i nie rozjechała się na dwie grupy. */}
                <datalist id="offer-group-labels">
                  {groupSuggestions.map(g => <option key={g} value={g}/>)}
                </datalist>
                {items.length === 0 && (
                  <div className="text-ink-400 text-[12px] text-center py-3">Brak pozycji — dodaj ze słownika lub ad-hoc.</div>
                )}
                {items.map((li, i) => {
                  const lineNet = round2((Number(li.quantity) || 0) * (Number(li.unitPriceNet) || 0));
                  const hasStudy = li.studyType != null;
                  return (
                    <div key={i} className="border border-ink-200 rounded-lg p-3 flex flex-col gap-2 bg-ink-50/40">
                      {/* Nagłówek pozycji: numer, powiązanie z typem, wartość, usuń */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-[12px] text-ink-400">#{i + 1}</span>
                        {hasStudy ? (
                          <span className="pill pill-earth text-[10px] flex items-center gap-1"
                                title={li.studyType + (li.studyVariant ? ' / ' + li.studyVariant : '')}>
                            {li.studyType}{li.studyVariant ? ' · ' + li.studyVariant : ''}
                            <button type="button"
                                    onClick={() => updItem(i, {
                                      studyType: null, studyVariant: null, studyDeadline: null,
                                      // Pola terminu chowają się razem z typem — zostawienie
                                      // trybu QUARTER/TEXT dałoby ofertę nie do zapisania,
                                      // z komunikatem o polu, którego nie ma już na ekranie.
                                      deadlineMode: 'DATE', deadlineQuarter: null,
                                      deadlineYear: null, deadlineText: null,
                                    })}
                                    className="hover:text-red-500" title="Usuń powiązanie z typem opracowania">
                              <Icon name="x" className="w-3 h-3"/>
                            </button>
                          </span>
                        ) : li.groupLabel ? (
                          <span className="pill pill-water text-[10px]" title="Typ opracowania i termin ustawiasz przy grupie">
                            zlecenie z grupy
                          </span>
                        ) : <span className="text-ink-400 text-[11px] italic">— pozycja ad-hoc —</span>}
                        <span className="ml-auto font-mono tabular-nums text-[13px] font-semibold text-ink-800">{formatMoney(lineNet)} zł</span>
                        <button type="button" onClick={() => dupItem(i)}
                                className="btn btn-ghost btn-icon text-ink-400 hover:text-water-600" title="Duplikuj pozycję">
                          <Icon name="copy"/>
                        </button>
                        <button type="button" onClick={() => rmItem(i)} disabled={items.length === 1}
                                className="btn btn-ghost btn-icon text-ink-400 hover:text-red-500 disabled:opacity-30" title="Usuń pozycję">
                          <Icon name="x"/>
                        </button>
                      </div>
                      {/* Grupa — nad opisem, bo dotyczy całej pozycji, a nie jej liczb. */}
                      <label className="field">
                        <span className="field-label">
                          Grupa / studnia
                          <span className="text-ink-400 font-normal ml-1">— opcjonalne; pozycje z tą samą nazwą dostaną podsumę na piśmie</span>
                        </span>
                        <input list="offer-group-labels" value={li.groupLabel ?? ''}
                               onChange={e => updItem(i, { groupLabel: e.target.value || null })}
                               maxLength={160}
                               className="input !py-1 text-[13px]"
                               placeholder="np. Studnia S-1 (gł. 40 m)"/>
                      </label>
                      {/* Opis — pełna szerokość, zawija tekst (był nieczytelny w wąskiej kolumnie tabeli) */}
                      <textarea value={li.description} onChange={e => updItem(i, { description: e.target.value })}
                                rows={2} className="textarea !py-1.5 w-full text-[13px] leading-snug"
                                placeholder="Opis pozycji, np. Wiercenie otworu Ø150 do głębokości 40 m"/>
                      {/* Pola liczbowe/meta poniżej, zwarta siatka */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <label className="field">
                          <span className="field-label">Jednostka</span>
                          <input value={li.unit} onChange={e => updItem(i, { unit: e.target.value })} className="input !py-1 font-mono text-[12px]"/>
                        </label>
                        <label className="field">
                          <span className="field-label">Ilość</span>
                          <input type="number" step="0.001" value={li.quantity} onChange={e => updItem(i, { quantity: Number(e.target.value) })} className="input !py-1 font-mono text-[12px] text-right"/>
                        </label>
                        <label className="field">
                          <span className="field-label">Cena netto</span>
                          <input type="number" step="0.01" value={li.unitPriceNet} onChange={e => updItem(i, { unitPriceNet: Number(e.target.value) })} className="input !py-1 font-mono text-[12px] text-right"/>
                        </label>
                        <div className="field">
                          <span className="field-label">Termin</span>
                          {hasStudy ? (
                            <DeadlineFields compact
                              mode={li.deadlineMode ?? 'DATE'} onMode={m => updItem(i, { deadlineMode: m })}
                              date={li.studyDeadline ?? ''} onDate={v => updItem(i, { studyDeadline: v || null })}
                              quarter={li.deadlineQuarter ?? null} onQuarter={v => updItem(i, { deadlineQuarter: v })}
                              year={li.deadlineYear ?? null} onYear={v => updItem(i, { deadlineYear: v })}
                              text={li.deadlineText ?? ''} onText={v => updItem(i, { deadlineText: v || null })}
                            />
                          ) : (
                            <input disabled
                                   placeholder={li.groupLabel ? '— z grupy —' : '—'}
                                   className="input !py-1 text-[11px] opacity-50"
                                   title={li.groupLabel
                                     ? 'Termin ustawiasz przy grupie — to z niej powstaje zlecenie'
                                     : 'Termin dostępny po powiązaniu pozycji z typem opracowania'}/>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <button type="button" onClick={addItem} className="btn btn-secondary btn-sm self-start">
                  <Icon name="plus" className="w-3.5 h-3.5"/> Dodaj pozycję ad-hoc
                </button>
              </div>
              <div className="card-body grid grid-cols-3 gap-3 border-t border-ink-100">
                <label className="field">
                  <span className="field-label">VAT [%]</span>
                  <input type="number" step="0.01" value={vatRate} onChange={e => setVatRate(Number(e.target.value))} className="input font-mono"/>
                </label>
                <label className="flex items-center gap-2 text-[12px] text-ink-700 col-span-2 cursor-pointer">
                  <input type="checkbox" checked={showGross} onChange={e => setShowGross(e.target.checked)}/>
                  Pokaż kwotę brutto na ofercie
                  <span className="text-ink-400">(odznaczone: „Razem” tylko netto)</span>
                </label>
              </div>
            </div>
          )}

          {pricePickerOpen && (
            <PricePickerModal
              prices={priceCatalog}
              onClose={() => setPricePickerOpen(false)}
              onPick={async p => {
                // Czy ten typ/wariant już jest w pozycjach? Nie blokujemy, ostrzegamy.
                const exists = items.some(it => it.studyType === p.studyType && it.studyVariant === p.variant);
                if (exists && !confirm(`Pozycje dla ${p.studyTypeName} / ${variantLabel(p.variant)} już są w ofercie. Dodać ponownie?`)) return;
                setPricePickerOpen(false);
                // Podpowiedz cały zestaw pozycji dla wariantu; fallback do jednej pozycji z cennika.
                let toAdd: OfferLineItemInput[];
                try {
                  const sug = await studyVariantItems.suggest(p.studyType, p.variant);
                  toAdd = sug.length > 0 ? itemsFromSuggestions(sug, p.studyType, p.variant) : [itemFromPrice(p)];
                } catch {
                  toAdd = [itemFromPrice(p)];
                }
                // Zastąp pierwszy pusty wiersz zestawem, resztę dopisz na końcu.
                setItems(prev => {
                  const idx = prev.findIndex(it => !it.studyType && !it.description.trim());
                  if (idx >= 0) { const copy = [...prev]; copy.splice(idx, 1, ...toAdd); return copy; }
                  return [...prev, ...toAdd];
                });
              }}
            />
          )}

          {fieldWorkPickerOpen && (
            <FieldWorkPickerModal
              kinds={fieldWorkCatalog}
              onClose={() => setFieldWorkPickerOpen(false)}
              onPick={k => {
                setFieldWorkPickerOpen(false);
                // Bez ceny w cenniku wstawiamy 0 — pozycja jest, cenę uzupełnia się w wierszu.
                const item: OfferLineItemInput = {
                  ...blankItem(), description: k.name, unit: k.unit, quantity: 1, unitPriceNet: Number(k.unitPriceNet ?? 0),
                };
                setItems(prev => {
                  const idx = prev.findIndex(it => !it.studyType && !it.description.trim());
                  if (idx >= 0) { const copy = [...prev]; copy.splice(idx, 1, item); return copy; }
                  return [...prev, item];
                });
              }}
            />
          )}

          {lumpPickerOpen && (
            <PricePickerModal
              prices={priceCatalog}
              onClose={() => setLumpPickerOpen(false)}
              onPick={p => {
                // Tryb LUMP_SUM — wybór jednej pozycji ustawia wszystkie pola ryczałtu naraz.
                setLumpStudyType(p.studyType);
                setLumpStudyVariant(p.variant);
                setUnitPrice(Number(p.unitPriceNet));
                setUnitCount(Number(p.defaultQuantity ?? 1));
                setUnitLabel(p.unit);
                setLumpPickerOpen(false);
              }}
            />
          )}

          {step === 3 && (
            <>
              <div className="card">
                <div className="card-head"><div className="card-title">Daty</div></div>
                <div className="card-body grid grid-cols-2 gap-3">
                  <label className="field">
                    <span className="field-label">Data wystawienia *</span>
                    <input type="date" value={issueDate} onChange={e => setIssueDate(e.target.value)} className="input"/>
                  </label>
                  <label className="field">
                    <span className="field-label">Ważna do</span>
                    <input type="date" value={validUntil} onChange={e => setValidUntil(e.target.value)} className="input"/>
                  </label>
                </div>
              </div>
              <div className="card">
                <div className="card-head"><div className="card-title">Termin / forma opracowania</div></div>
                <div className="card-body">
                  <TiptapEditor value={termsHtml} onChange={setTermsHtml} minHeight="120px"/>
                </div>
              </div>
              <div className="card">
                <div className="card-head"><div className="card-title">Warunki płatności</div></div>
                <div className="card-body">
                  <input value={paymentTerms} onChange={e => setPaymentTerms(e.target.value)} className="input"/>
                </div>
              </div>
              <div className="card">
                <div className="card-head"><div className="card-title">Podpis</div></div>
                <div className="card-body">
                  <label className="flex items-center gap-2 text-[13px] text-ink-800 cursor-pointer">
                    <input type="checkbox" checked={includeSignature}
                           disabled={!selectedCompany?.signatureUrl}
                           onChange={e => setIncludeSignature(e.target.checked)}/>
                    Wstaw pieczątkę z podpisem
                  </label>
                  {selectedCompany?.signatureUrl ? (
                    <img src={selectedCompany.signatureUrl} alt="Pieczątka z podpisem"
                         className={`mt-2 max-h-[60px] max-w-[200px] object-contain object-left ${includeSignature ? '' : 'opacity-30'}`}/>
                  ) : (
                    <div className="text-[11px] text-ink-400 mt-1">
                      Firma {selectedCompany?.shortName ?? ''} nie ma wgranej pieczątki —
                      dodaj ją w Konfiguracja → Firmy.
                    </div>
                  )}
                </div>
              </div>
              <div className="card">
                <div className="card-head"><div className="card-title">Uwagi wewnętrzne (nie drukowane)</div></div>
                <div className="card-body">
                  <textarea value={notes} onChange={e => setNotes(e.target.value)} className="textarea" rows={3}/>
                </div>
              </div>
            </>
          )}

          {step === 4 && (
            <div className="card">
              <div className="card-head">
                <div className="card-title">Podgląd</div>
                <div className="text-[11px] text-ink-400 ml-auto">dokładnie to trafi do PDF i DOCX</div>
              </div>
              <div className="card-body bg-ink-50 p-6">
                {previewErr && <div className="text-[13px] text-red-600">{previewErr}</div>}
                {!previewErr && !previewDoc && <div className="text-[13px] text-ink-400">Składanie pisma…</div>}
                {!previewErr && previewDoc && <OfferDocumentView doc={previewDoc}/>}
              </div>
            </div>
          )}

          <div className="flex justify-between gap-2">
            <button onClick={() => nav('/oferty')} className="btn btn-secondary">Anuluj</button>
            <div className="flex gap-2">
              {step > 0 && <button onClick={prev} className="btn btn-secondary">Wstecz</button>}
              {step < STEPS.length - 1 && <button onClick={next} className="btn btn-primary">Dalej</button>}
              {step === STEPS.length - 1 && (
                <button disabled={busy} onClick={submit} className="btn btn-primary">
                  {busy ? 'Zapisywanie…' : (isEdit ? 'Zapisz zmiany' : 'Utwórz ofertę')}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="card">
            <div className="card-head"><div className="card-title">Podsumowanie kwot</div></div>
            <div className="card-body flex flex-col gap-2 text-[13px]">
              <div className="flex justify-between"><span className="text-ink-500">Netto</span><span className="font-mono tabular-nums text-ink-800">{formatMoney(totals.net)} zł</span></div>
              <div className="flex justify-between"><span className="text-ink-500">VAT ({vatRate}%)</span><span className="font-mono tabular-nums text-ink-800">{formatMoney(totals.vat)} zł</span></div>
              <hr className="border-0 border-t border-ink-100 my-1"/>
              <div className="flex justify-between font-semibold text-[14px]"><span className="text-ink-900">Brutto</span><span className="font-mono tabular-nums text-ink-900">{formatMoney(totals.gross)} zł</span></div>
              {/* Ta karta to widok WEWNĘTRZNY — na piśmie kwoty mogą być węższe (V160). */}
              <div className="text-[11px] text-ink-400 mt-1">
                Na dokumencie: {showGross ? 'netto, VAT i brutto' : 'tylko netto'}
              </div>
            </div>
          </div>
          {selectedCompany && (
            <div className="card">
              <div className="card-head"><div className="card-title">Wystawiana z firmy</div></div>
              <div className="card-body text-[12px] text-ink-700 flex items-center gap-3">
                {selectedCompany.logoUrl && <img src={selectedCompany.logoUrl} alt="" className="h-10 max-w-[80px] object-contain"/>}
                <div>
                  <div className="font-semibold text-ink-900">{selectedCompany.shortName}</div>
                  <div className="text-ink-500 text-[11px]">{selectedCompany.issuePlace}</div>
                </div>
              </div>
            </div>
          )}
          {selectedClient && (
            <div className="card">
              <div className="card-head"><div className="card-title">Klient</div></div>
              <div className="card-body text-[12px] text-ink-700">
                <div className="font-semibold text-ink-900">
                  {selectedClient.name}
                  {selectedClient.city && <span className="font-normal text-ink-500"> · {selectedClient.city}</span>}
                </div>
                {selectedClient.nip && <div>NIP: {selectedClient.nip}</div>}
                {selectedClient.email && <div className="text-ink-500">{selectedClient.email}</div>}
              </div>
            </div>
          )}
          {selectedIntakes.length > 0 && (
            <div className="card">
              <div className="card-head"><div className="card-title">Ujęcia ({selectedIntakes.length})</div></div>
              <div className="card-body text-[12px] text-ink-700 flex flex-col gap-1">
                {selectedIntakes.map((u, idx) => (
                  <div key={u.id} className="flex items-center gap-1.5">
                    {idx === 0 && selectedIntakes.length > 1 && <span className="text-earth-500" title="Ujęcie główne">★</span>}
                    <span className="font-mono text-[11px] text-ink-400">{u.number}</span>
                    <span className="text-ink-900">{u.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <ClientFormModal
        open={clientModalOpen}
        initial={null}
        onClose={() => setClientModalOpen(false)}
        onSaved={(c) => { setClients([...clients.filter(x => x.id !== c.id), c].sort((a, b) => a.name.localeCompare(b.name))); setClientId(c.id); }}
      />
      {/* Nowa lista klientów odpala efekt adresata — jeśli użytkownik nie zmieniał go ręcznie,
          adres z kartoteki trafia od razu do pisma. */}
      <ClientFormModal
        open={clientEditOpen}
        initial={selectedClient ?? null}
        onClose={() => setClientEditOpen(false)}
        onSaved={(c) => setClients(prev => prev.map(x => (x.id === c.id ? c : x)))}
      />
    </div>
  );
}

/** Modal wyboru pozycji ze słownika cen — klik wiersza dodaje line item z prefill. */
/** Mapowanie StudyType → grupa nadrzędna (top level w drzewie picker-a). */
function studyTypeGroupName(studyType: string): string {
  if (studyType.startsWith('OP_WOD_')) return 'Operat wodnoprawny';
  if (studyType.startsWith('PRG_')) return 'Projekt robót geologicznych';
  if (studyType.startsWith('DOK_HYDRO_')) return 'Dokumentacja hydrogeologiczna';
  if (studyType === 'DOK_LIKW_5') return 'Dokumentacja geologiczna z likwidacji';
  if (studyType === 'KIP_2') return 'Karta informacyjna przedsięwzięcia';
  if (studyType === 'ANALIZA_RYZYKA_6') return 'Analiza ryzyka';
  if (studyType === 'PLAN_BEZP_7') return 'Plan bezpieczeństwa wody';
  if (studyType === 'PLAN_RUCHU_8') return 'Plan ruchu';
  return 'Inne';
}

/** Sortowanie grup w stałej kolejności (zgodnie z numeracją typów). */
const GROUP_SORT_ORDER = [
  'Projekt robót geologicznych',
  'Karta informacyjna przedsięwzięcia',
  'Operat wodnoprawny',
  'Dokumentacja hydrogeologiczna',
  'Dokumentacja geologiczna z likwidacji',
  'Analiza ryzyka',
  'Plan bezpieczeństwa wody',
  'Plan ruchu',
  'Inne',
];

/** Sub-nazwa typu w drzewie (poziom 2) — bez nazwy grupy nadrzędnej. */
function studyTypeSubName(studyType: string, fullName: string): string {
  // Większość pełnych nazw zawiera myślnik: "Operat wodnoprawny — pobór wód" → bierzemy część po myślniku
  const dashIdx = fullName.indexOf(' — ');
  if (dashIdx > 0) return fullName.substring(dashIdx + 3);
  return fullName;
}

/** Ludzka nazwa wariantu (poziom 3 — liść drzewa). */
function variantLabel(variant: string): string {
  switch (variant) {
    case 'SIMPLE': return 'wariant prosty';
    case 'COMPLEX': return 'wariant złożony';
    case 'VERY_COMPLEX': return 'wariant bardzo złożony';
    case 'VC_PUMPING': return 'z próbnym pompowaniem';
    case 'VC_MONITORING': return 'z monitoringiem';
    case 'VC_ZONES': return 'ze strefami ochronnymi';
    default: return variant.toLowerCase().replace(/_/g, ' ');
  }
}

/** Wybór rodzaju pracy z cennika prac terenowych — jedna pozycja kosztorysu z ceną z cennika. */
function FieldWorkPickerModal({ kinds, onClose, onPick }: {
  kinds: FieldWorkKindEntry[];
  onClose: () => void;
  onPick: (k: FieldWorkKindEntry) => void;
}) {
  const [filter, setFilter] = useState('');
  const q = filter.trim().toLowerCase();
  const filtered = kinds.filter(k => !q || k.name.toLowerCase().includes(q) || (k.description?.toLowerCase().includes(q) ?? false));
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-auto" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl max-w-[640px] w-full mt-8" onClick={e => e.stopPropagation()}>
        <div className="border-b border-ink-100 px-5 py-3 flex items-center gap-3">
          <h2 className="text-[16px] font-semibold m-0">Wybierz pracę z cennika prac terenowych</h2>
          <button onClick={onClose} className="ml-auto text-ink-400 hover:text-ink-700"><Icon name="x"/></button>
        </div>
        <div className="p-3 border-b border-ink-100">
          <input value={filter} onChange={e => setFilter(e.target.value)}
                 placeholder="Filtruj po nazwie…" className="input w-full" autoFocus/>
        </div>
        <div className="max-h-[65vh] overflow-y-auto p-2">
          {filtered.length === 0 && (
            <div className="p-4 text-ink-400">
              {kinds.length === 0
                ? 'Cennik jest pusty. Admin: dodaj rodzaje w Administracja → Cennik prac terenowych.'
                : 'Brak rodzajów pasujących do filtra.'}
            </div>
          )}
          {filtered.map(k => (
            <button key={k.id} type="button" onClick={() => onPick(k)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-water-50 text-left">
              <Icon name="chevron" className="w-3 h-3 text-ink-300 -rotate-90"/>
              <span className="text-[13px] text-ink-800 flex-1 truncate" title={k.description ?? ''}>{k.name}</span>
              <span className="text-[11px] text-ink-500">{k.unit}</span>
              <span className="font-mono text-[12px] tabular-nums text-ink-800 w-28 text-right">
                {k.unitPriceNet != null ? `${formatMoney(k.unitPriceNet)} zł` : <span className="text-ink-300">bez ceny</span>}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function PricePickerModal({ prices, onClose, onPick }: {
  prices: StudyTypePrice[];
  onClose: () => void;
  onPick: (p: StudyTypePrice) => void;
}) {
  const [filter, setFilter] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const matches = (p: StudyTypePrice): boolean => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return p.studyTypeName.toLowerCase().includes(q)
        || p.variant.toLowerCase().includes(q)
        || p.studyType.toLowerCase().includes(q)
        || variantLabel(p.variant).toLowerCase().includes(q)
        || studyTypeGroupName(p.studyType).toLowerCase().includes(q)
        || (p.description?.toLowerCase().includes(q) ?? false);
  };

  const filtered = prices.filter(matches);

  // Budujemy drzewo: Grupa → StudyType → wariant
  const tree = new Map<string, Map<string, StudyTypePrice[]>>();
  for (const p of filtered) {
    const g = studyTypeGroupName(p.studyType);
    if (!tree.has(g)) tree.set(g, new Map());
    const sub = tree.get(g)!;
    if (!sub.has(p.studyType)) sub.set(p.studyType, []);
    sub.get(p.studyType)!.push(p);
  }

  // Sort grup wg ustalonej kolejności + sub-typów wg sortOrder
  const sortedGroups = Array.from(tree.keys()).sort((a, b) => {
    const ai = GROUP_SORT_ORDER.indexOf(a);
    const bi = GROUP_SORT_ORDER.indexOf(b);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });

  const toggleCollapsed = (key: string) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Z aktywnym filtrem nigdy nie zwijamy — pokazujemy wszystkie pasujące pozycje
  const isCollapsed = (key: string) => !filter.trim() && collapsed.has(key);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-auto" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl max-w-[900px] w-full mt-8" onClick={e => e.stopPropagation()}>
        <div className="border-b border-ink-100 px-5 py-3 flex items-center gap-3">
          <h2 className="text-[16px] font-semibold m-0">Wybierz opracowanie ze słownika cen</h2>
          <button onClick={onClose} className="ml-auto text-ink-400 hover:text-ink-700"><Icon name="x"/></button>
        </div>
        <div className="p-3 border-b border-ink-100">
          <input
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Filtruj po nazwie, grupie lub wariancie…"
            className="input w-full"
            autoFocus
          />
        </div>
        <div className="max-h-[65vh] overflow-y-auto p-2">
          {sortedGroups.length === 0 && (
            <div className="p-4 text-ink-400">
              {prices.length === 0
                ? 'Słownik jest pusty. Admin: dodaj pozycje w Administracja → Słowniki opracowań → Cennik.'
                : 'Brak pozycji pasujących do filtra.'}
            </div>
          )}
          {sortedGroups.map(group => {
            const subTypes = tree.get(group)!;
            const totalInGroup = Array.from(subTypes.values()).reduce((s, arr) => s + arr.length, 0);
            const groupCollapsed = isCollapsed('g:' + group);
            return (
              <div key={group} className="mb-1">
                <button type="button"
                        onClick={() => toggleCollapsed('g:' + group)}
                        className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-ink-50 text-left">
                  <Icon name={groupCollapsed ? 'chevron' : 'chevron'}
                        className={'w-3 h-3 text-ink-400 transition-transform ' + (groupCollapsed ? '' : 'rotate-90')}/>
                  <span className="font-semibold text-[13px] text-ink-900">{group}</span>
                  <span className="text-[11px] text-ink-400 font-mono">{totalInGroup}</span>
                </button>
                {!groupCollapsed && Array.from(subTypes.entries())
                  .sort(([, a], [, b]) => (a[0]?.sortOrder ?? 100) - (b[0]?.sortOrder ?? 100))
                  .map(([studyType, variants]) => {
                    const subName = studyTypeSubName(studyType, variants[0].studyTypeName);
                    const typeCollapsed = isCollapsed('t:' + studyType);
                    return (
                      <div key={studyType}>
                        <button type="button"
                                onClick={() => toggleCollapsed('t:' + studyType)}
                                className="w-full flex items-center gap-2 px-2 py-1 ml-5 rounded hover:bg-ink-50 text-left">
                          <Icon name="chevron"
                                className={'w-3 h-3 text-ink-400 transition-transform ' + (typeCollapsed ? '' : 'rotate-90')}/>
                          <span className="text-[12px] text-ink-700">{subName}</span>
                          <span className="font-mono text-[10px] text-ink-400">{studyType}</span>
                          <span className="text-[10px] text-ink-400">· {variants.length}</span>
                        </button>
                        {!typeCollapsed && variants
                          .sort((a, b) => a.sortOrder - b.sortOrder)
                          .map(p => (
                          <button key={p.id} type="button"
                                  onClick={() => onPick(p)}
                                  className="w-full flex items-center gap-2 px-2 py-1 ml-12 rounded hover:bg-water-50 text-left group">
                            <Icon name="chevron" className="w-3 h-3 text-ink-300 -rotate-90"/>
                            <span className="text-[12px] text-ink-800 flex-1 truncate"
                                  title={p.description ?? ''}>
                              {variantLabel(p.variant)}
                            </span>
                            <span className="font-mono text-[10px] text-ink-400">{p.variant}</span>
                            <span className="text-[11px] text-ink-500">{p.unit}</span>
                            <span className="font-mono text-[12px] tabular-nums text-ink-800 w-24 text-right">
                              {formatMoney(p.unitPriceNet)} zł
                            </span>
                          </button>
                        ))}
                      </div>
                    );
                  })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * Termin realizacji: data | kwartał | opis słowny (V160).
 *
 * Wariant `compact` wchodzi w wąską kolumnę „Termin" w tabeli pozycji kosztorysu.
 * Świadomie NIE czyścimy pól przy zmianie trybu — użytkownik przełączający się tam
 * i z powrotem nie traci tego, co już wpisał; serwer i tak zapisuje tylko składowe
 * pasujące do wybranego trybu.
 */
const MODES: { v: OfferDeadlineMode; label: string }[] = [
  { v: 'DATE', label: 'Data' },
  { v: 'QUARTER', label: 'Kwartał' },
  { v: 'TEXT', label: 'Opis' },
];
const KWARTALY = ['I', 'II', 'III', 'IV'];

function DeadlineFields({
  label, hint, compact, mode, onMode, date, onDate, quarter, onQuarter, year, onYear, text, onText,
}: {
  label?: string;
  hint?: string;
  compact?: boolean;
  mode: OfferDeadlineMode;
  onMode: (m: OfferDeadlineMode) => void;
  date: string;
  onDate: (v: string) => void;
  quarter: number | null;
  onQuarter: (v: number | null) => void;
  year: number | null;
  onYear: (v: number | null) => void;
  text: string;
  onText: (v: string) => void;
}) {
  const inputCls = compact ? 'input !py-1 font-mono text-[11px]' : 'input font-mono';
  const thisYear = new Date().getFullYear();

  return (
    <div className="flex flex-col gap-1">
      {label && <span className="field-label">{label}</span>}
      <div className="flex gap-1">
        {MODES.map(m => (
          <button key={m.v} type="button" onClick={() => onMode(m.v)}
            className={`px-2 py-0.5 rounded text-[11px] border ${
              mode === m.v
                ? 'bg-water-100 border-water-300 text-water-800 font-medium'
                : 'bg-white border-ink-200 text-ink-500 hover:border-ink-300'}`}>
            {m.label}
          </button>
        ))}
      </div>

      {mode === 'DATE' && (
        <input type="date" value={date} onChange={e => onDate(e.target.value)} className={inputCls}/>
      )}

      {mode === 'QUARTER' && (
        <div className="flex gap-1">
          <SearchableSelect
            value={quarter == null ? '' : String(quarter)}
            onChange={v => onQuarter(v ? Number(v) : null)}
            options={KWARTALY.map((rzym, i) => ({ value: String(i + 1), label: `${rzym} kwartał` }))}
            placeholder="kwartał…"
            searchThreshold={10}
            className={inputCls + ' flex-1'}
          />
          <input type="number" min={2000} max={2100} placeholder={String(thisYear)}
                 value={year ?? ''} onChange={e => onYear(e.target.value ? Number(e.target.value) : null)}
                 className={inputCls + (compact ? ' w-[68px]' : ' w-[100px]')}/>
        </div>
      )}

      {mode === 'TEXT' && (
        <input value={text} onChange={e => onText(e.target.value)} maxLength={200}
               placeholder="np. po uzyskaniu decyzji o warunkach zabudowy"
               className={compact ? 'input !py-1 text-[11px]' : 'input'}/>
      )}

      {hint && !compact && <div className="text-[11px] text-ink-400">{hint}</div>}
    </div>
  );
}
